import { useWorkstation } from './WorkstationSettings';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, buildKioskAuthHeaders, resolveMediaUrl } from './api';
import { printOrderTickets, buildMenuPriceLines } from './thermalPrinter';
import NumericKeypad from './NumericKeypad';
import { paymentDisplayMessage } from './paymentDisplay';
import { clearCustomerSession, getCustomerSessionAccountId, readCustomerSession, refreshCustomerSession } from './kioskCustomerSession';
import TerminalPaymentDialog from './TerminalPaymentDialog';
import { getTerminalConfig, readPendingTerminalPayment, persistPendingTerminalPayment, clearPendingTerminalPayment, readTerminalReader, getTerminalScope } from './terminalPayments';

const terminalFetch = (url, options = {}) => fetch(url, { ...options, headers: buildKioskAuthHeaders(options.headers) });

const MENU_TAB_ID = 'menu';
const DEALS_TAB_ID = 'deals';
const KIOSK_BOOTSTRAP_CACHE_KEY = 'maythai_kiosk_bootstrap_cache_v2';
const DEFAULT_DEGRADED_MESSAGE = 'Mode degrade: le catalogue reste visible, mais l’envoi de commande est temporairement indisponible.';
const SECTION_EMOJIS = {
  'Deals du jour': '🔥',
  'Entrée': '🥗',
  'Plat': '🍜',
  'Dessert': '🍰',
  'Boisson': '🥤',
  Side: '🍟',
  Menu: '🍱',
};

const PRODUCT_VISUAL_TAGS = [
  { key: 'isSpicy', label: 'Epicé', emoji: '🌶️' },
  { key: 'isVegetarian', label: 'Veggie', emoji: '🥬' },
  { key: 'isHalal', label: 'Halal', emoji: '🕌' },
  { key: 'isGlutenFree', label: 'Sans gluten', emoji: '🌾' },
];
const PRODUCT_ALLERGEN_LABELS = {
  gluten: 'Céréales contenant du gluten',
  crustaceans: 'Crustacés',
  eggs: 'Œufs',
  fish: 'Poissons',
  peanuts: 'Arachides',
  soy: 'Soja',
  milk: 'Lait',
  nuts: 'Fruits à coque',
  celery: 'Céleri',
  mustard: 'Moutarde',
  sesame: 'Graines de sésame',
  sulphites: 'Anhydride sulfureux et sulfites',
  lupin: 'Lupin',
  molluscs: 'Mollusques',
};

function getCanonicalSectionName(name) {
  const normalized = String(name || '').trim().toLowerCase();

  if (['entrée', 'entree', 'entrées', 'entrees'].includes(normalized)) return 'Entrée';
  if (['plat', 'plats'].includes(normalized)) return 'Plat';
  if (['dessert', 'desserts'].includes(normalized)) return 'Dessert';
  if (['boisson', 'boissons'].includes(normalized)) return 'Boisson';
  if (['side', 'sides'].includes(normalized)) return 'Side';
  return String(name || '').trim();
}

function getStoredBootstrapCache() {
  try {
    const raw = localStorage.getItem(KIOSK_BOOTSTRAP_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    const accountId = JSON.parse(localStorage.getItem('kioskAccount') || '{}')?.id;
    return accountId && parsed?.accountId === String(accountId) ? parsed : null;
  } catch (error) {
    return null;
  }
}

function saveBootstrapCache(payload) {
  try {
    const accountId = JSON.parse(localStorage.getItem('kioskAccount') || '{}')?.id;
    if (!accountId) return;
    localStorage.setItem(KIOSK_BOOTSTRAP_CACHE_KEY, JSON.stringify({
      accountId: String(accountId),
      savedAt: new Date().toISOString(),
      payload,
    }));
  } catch (error) {
    // Ignore cache persistence issues on kiosk devices.
  }
}

function buildMyposReceiptFromStatus(statusPayload = {}, total = 0) {
  const mypos = statusPayload?.mypos || {};
  const order = statusPayload?.order || {};
  if (String(mypos.state || order.myposStatus || '').trim().toLowerCase() !== 'approved') {
    return null;
  }
  return {
    state: 'approved',
    cardBrand: mypos.cardBrand || order.myposCardScheme || '',
    maskedPan: mypos.maskedPan || order.myposMaskedPan || '',
    amount: mypos.amount || order.totalTtc || total,
    currency: mypos.currency || 'EUR',
    authCode: mypos.authCode || order.myposAuthCode || '',
    rrn: mypos.rrn || order.myposRrn || '',
    stan: mypos.stan || '',
    tid: mypos.tid || '',
    mid: mypos.mid || '',
    aid: mypos.aid || '',
    paymentReference: mypos.paymentReference || order.myposPaymentReference || '',
    merchantTransactionDate: mypos.merchantTransactionDate || '',
    merchantTransactionTime: mypos.merchantTransactionTime || '',
  };
}

function getOptimizedProductImage(image) {
  return resolveMediaUrl(image, { width: 420, height: 280, quality: 60, fit: 'cover' });
}

function getOptimizedProductDetailImage(image) {
  return resolveMediaUrl(image, { width: 900, height: 600, quality: 78, fit: 'cover' });
}

function getOptimizedRecentProductImage(image) {
  return resolveMediaUrl(image, { width: 112, height: 112, quality: 58, fit: 'cover' });
}

function getOptimizedSupplementImage(image) {
  return resolveMediaUrl(image, { width: 80, height: 80, quality: 38, fit: 'cover' });
}

function getOptimizedCartImage(image) {
  return resolveMediaUrl(image, { width: 96, height: 96, quality: 56, fit: 'cover' });
}

function getOptimizedCategoryButtonImage(image) {
  return resolveMediaUrl(image, { width: 420, height: 180, quality: 72, fit: 'cover' });
}

function getOptimizedSubcategoryButtonImage(image) {
  return resolveMediaUrl(image, { width: 420, height: 120, quality: 70, fit: 'cover' });
}

function getOptimizedSubcategoryBannerImage(image) {
  return resolveMediaUrl(image, { width: 640, height: 260, quality: 72, fit: 'cover' });
}

function enrichSupplementMedia(supplement) {
  const fullImage = resolveMediaUrl(supplement?.image);
  return {
    ...supplement,
    image: fullImage,
    thumbnailImage: getOptimizedSupplementImage(supplement?.image) || fullImage,
  };
}

function enrichProductMedia(product) {
  const fullImage = resolveMediaUrl(product?.image);
  return {
    ...product,
    image: fullImage,
    thumbnailImage: getOptimizedProductImage(product?.image) || fullImage,
    detailImage: getOptimizedProductDetailImage(product?.image) || fullImage,
    cartImage: getOptimizedCartImage(product?.image) || fullImage,
    Supplements: Array.isArray(product?.Supplements)
      ? product.Supplements.map((supplement) => enrichSupplementMedia(supplement))
      : [],
  };
}

function enrichRecentProductMedia(product) {
  const fullImage = resolveMediaUrl(product?.image);
  return {
    ...product,
    image: fullImage,
    thumbnailImage: getOptimizedRecentProductImage(product?.image) || fullImage,
    detailImage: getOptimizedProductDetailImage(product?.image) || fullImage,
    cartImage: getOptimizedCartImage(product?.image) || fullImage,
  };
}

function getProductTags(product) {
  if (Array.isArray(product?.tags) && product.tags.length > 0) {
    return product.tags;
  }

  return PRODUCT_VISUAL_TAGS.filter((tag) => product?.[tag.key]).map(({ key, ...tag }) => ({
    id: key,
    ...tag,
  }));
}

function parseAvailabilityTime(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) {
    return null;
  }

  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null;
  }

  return hours * 60 + minutes;
}

function isWithinAvailabilityWindow(record, now = new Date()) {
  if (!record) {
    return false;
  }

  if (record.isAvailable === false) {
    return false;
  }

  const fromMinutes = parseAvailabilityTime(record.availableFromTime);
  const toMinutes = parseAvailabilityTime(record.availableToTime);
  const currentMinutes = now.getHours() * 60 + now.getMinutes();

  if (fromMinutes === null && toMinutes === null) {
    return true;
  }

  if (fromMinutes !== null && toMinutes === null) {
    return currentMinutes >= fromMinutes;
  }

  if (fromMinutes === null && toMinutes !== null) {
    return currentMinutes <= toMinutes;
  }

  if (fromMinutes === toMinutes) {
    return true;
  }

  if (fromMinutes < toMinutes) {
    return currentMinutes >= fromMinutes && currentMinutes <= toMinutes;
  }

  return currentMinutes >= fromMinutes || currentMinutes <= toMinutes;
}

function formatAvailabilityWindow(record) {
  const fromTime = String(record?.availableFromTime || '').trim();
  const toTime = String(record?.availableToTime || '').trim();

  if (!fromTime && !toTime) {
    return '';
  }

  if (fromTime && toTime) {
    return `${fromTime} - ${toTime}`;
  }

  return fromTime ? `dès ${fromTime}` : `jusqu'à ${toTime}`;
}

function getSectionRequiredQuantity(section) {
  return Math.max(1, parseInt(section?.MenuSections?.requiredQuantity || section?.requiredQuantity || 1, 10) || 1);
}

function getMenuSectionSelections(menuSelections, sectionId) {
  return Array.isArray(menuSelections?.[sectionId]) ? menuSelections[sectionId] : [];
}

function getSectionSelectionLabel(sectionName, quantity) {
  const safeName = String(sectionName || 'catégorie').trim().toLowerCase();
  return `Choisis ${quantity} ${safeName}${quantity > 1 ? 's' : ''}`;
}

function getMenuSelectionShortageMessage(section, selectedCount) {
  const requiredQuantity = getSectionRequiredQuantity(section);
  const missingCount = Math.max(0, requiredQuantity - selectedCount);
  if (!missingCount) {
    return '';
  }

  const sectionLabel = `${section?.name || 'catégorie'}${missingCount > 1 ? 's' : ''}`;
  return `Il manque ${missingCount} ${sectionLabel} à choisir.`;
}

function formatEuro(value) {
  return `${Number(value || 0).toFixed(2)} €`;
}

function ClientView() {
  const workstation = useWorkstation();
  const navigate = useNavigate();
  const ADMIN_MENU_PASSWORD = process.env.REACT_APP_ADMIN_MENU_PASSWORD || '';
  const inactivityTimerRef = useRef(null);
  const cartFeedbackTimeoutRef = useRef(null);
  const [sections, setSections] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [menus, setMenus] = useState([]);
  const [printers, setPrinters] = useState([]);
  const [productionZones, setProductionZones] = useState([]);
  const [cart, setCart] = useState([]);
  const [selectedMenu, setSelectedMenu] = useState(null);
  const [menuSelections, setMenuSelections] = useState({});
  const [menuSelectionError, setMenuSelectionError] = useState('');
  const [supplementProduct, setSupplementProduct] = useState(null);
  const [selectedSupplementIds, setSelectedSupplementIds] = useState([]);
  const [upsellProducts, setUpsellProducts] = useState([]);
  const [activeTab, setActiveTab] = useState(null);
  const [activeSubcategoryId, setActiveSubcategoryId] = useState('all');
  const [showCart, setShowCart] = useState(false);
  const [showOrderOptions, setShowOrderOptions] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [bootstrapReloadToken, setBootstrapReloadToken] = useState(0);
  const [kioskConnectionState, setKioskConnectionState] = useState(
    typeof navigator === 'undefined' || navigator.onLine ? 'online' : 'offline'
  );
  const [kioskConnectionMessage, setKioskConnectionMessage] = useState('');
  const [cachedCatalogTimestamp, setCachedCatalogTimestamp] = useState('');
  const [checkoutState, setCheckoutState] = useState('idle');
  const [checkoutMessage, setCheckoutMessage] = useState('');
  const [inactivityTimeoutSeconds, setInactivityTimeoutSeconds] = useState(20);
  const [checkoutSuccessMessage, setCheckoutSuccessMessage] = useState('Merci pour votre commande.');
  const [checkoutSuccessCashMessage, setCheckoutSuccessCashMessage] = useState('Merci pour votre commande.');
  const [checkoutSuccessCardMessage, setCheckoutSuccessCardMessage] = useState('Merci pour votre commande.');
  const [checkoutSuccessMessageSize, setCheckoutSuccessMessageSize] = useState(54);
  const [checkoutSuccessMessageDurationSeconds, setCheckoutSuccessMessageDurationSeconds] = useState(2);
  const [ticketHeaderText, setTicketHeaderText] = useState('');
  const [ticketHeaderCashText, setTicketHeaderCashText] = useState('');
  const [ticketHeaderCardPaidText, setTicketHeaderCardPaidText] = useState('');
  const [ticketLogoMedia, setTicketLogoMedia] = useState('');
  const [ticketStoreLine1, setTicketStoreLine1] = useState('MAYTHAI');
  const [ticketStoreLine2, setTicketStoreLine2] = useState('32 avenue de la Republique');
  const [ticketStoreLine3, setTicketStoreLine3] = useState('75011 Paris');
  const [ticketStoreLine4, setTicketStoreLine4] = useState('TVA : - NAF : 5610A');
  const [ticketStoreLine5, setTicketStoreLine5] = useState('Tel: 01 47 00 19 65');
  const [ticketStoreLine6, setTicketStoreLine6] = useState('');
  const [ticketQrText, setTicketQrText] = useState('');
  const [ticketQrUrl, setTicketQrUrl] = useState('');
  const [ticketFooterLine1, setTicketFooterLine1] = useState('Merci de votre visite');
  const [ticketFooterLine2, setTicketFooterLine2] = useState('A bientot !');
  const [ticketFooterLine3, setTicketFooterLine3] = useState('');
  const [ticketHeaderTextSize, setTicketHeaderTextSize] = useState(4);
  const [clientTicketZoneId, setClientTicketZoneId] = useState('');
  const [clientTicketCopies, setClientTicketCopies] = useState(1);
  const [pendingTicketNumber, setPendingTicketNumber] = useState(null);
  const pendingOrderRef = useRef(null);
  const orderRequestRef = useRef(null);
  const purchaseRequestRef = useRef(null);
  const uncertainOrderRef = useRef(false);
  const checkoutRequestIdRef = useRef(null);
  const checkoutOrderedAtRef = useRef(null);
  const rememberPendingOrder = (order) => {
    pendingOrderRef.current = order;
  };
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [showPaymentChoice, setShowPaymentChoice] = useState(false);
  const [myposEnabled, setMyposEnabled] = useState(false);
  const [terminalConfig, setTerminalConfig] = useState(null);
  const [terminalConfigError, setTerminalConfigError] = useState('');
  const [terminalOrderId, setTerminalOrderId] = useState(null);
  const [terminalPrintError, setTerminalPrintError] = useState('');
  const terminalScopeRef = useRef(getTerminalScope());
  const terminalPrintingRef = useRef(false);
  const [uiPrimaryColor, setUiPrimaryColor] = useState('#08383a');
  const [uiAccentColor, setUiAccentColor] = useState('#ccb08c');
  const [uiTextColor, setUiTextColor] = useState('#2b180f');
  const [uiBackgroundColor, setUiBackgroundColor] = useState('#f7f0e4');
  const [uiSurfaceColor, setUiSurfaceColor] = useState('rgba(255, 250, 243, 0.96)');
  const [uiMutedColor, setUiMutedColor] = useState('#7d6757');
  const [uiCategoryTextSize, setUiCategoryTextSize] = useState(18);
  const [uiProductNameTextSize, setUiProductNameTextSize] = useState(18);
  const [uiHeadingTextSize, setUiHeadingTextSize] = useState(28);
  const [uiBodyTextSize, setUiBodyTextSize] = useState(17);
  const [customerComment, setCustomerComment] = useState('');
  const [includeCutlery, setIncludeCutlery] = useState(false);
  const [useLoyaltyDiscount, setUseLoyaltyDiscount] = useState(false);
  const [, updateCustomerScope] = useState(0);
  useEffect(() => {
    const update = () => updateCustomerScope((value) => value + 1);
    window.addEventListener('storage', update);
    window.addEventListener('focus', update);
    return () => { window.removeEventListener('storage', update); window.removeEventListener('focus', update); };
  }, []);
  const customerAccountId = getCustomerSessionAccountId();
  useEffect(() => {
    let active = true;
    const scope = getTerminalScope();
    const changedScope = terminalScopeRef.current !== scope;
    terminalScopeRef.current = scope;
    setTerminalConfig(null);
    setTerminalConfigError('');
    setTerminalPrintError('');
    if (changedScope) {
      setCart([]);
      rememberPendingOrder(null);
      checkoutRequestIdRef.current = null;
      uncertainOrderRef.current = false;
      setCheckoutState('idle');
      setSelectedPaymentMethod('');
    }
    const pending = readPendingTerminalPayment();
    setTerminalOrderId(pending?.orderId || null);
    if (pending?.orderId) {
      rememberPendingOrder({ id: pending.orderId });
      setCheckoutState('loading');
      setSelectedPaymentMethod('cb');
    }
    getTerminalConfig(terminalFetch).then((config) => {
      if (active && getTerminalScope() === scope) setTerminalConfig(config);
    }).catch(() => {
      if (active && getTerminalScope() === scope) setTerminalConfigError('Le paiement par carte est indisponible. Appelez un membre de l’équipe.');
    });
    return () => { active = false; };
    // The terminal context is always isolated to the current authenticated account.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAccountId]);
  const [customerState, setCustomerState] = useState(null);
  const currentCustomer = customerState?.accountId === customerAccountId ? customerState.customer : null;
  const customerRequestRef = useRef(0);
  const setCurrentCustomer = (customer) => setCustomerState(customer ? { accountId: customerAccountId, customer } : null);
  const [showCustomerAccount, setShowCustomerAccount] = useState(false);
  const [promoCodeInput, setPromoCodeInput] = useState('');
  const [appliedPromoCode, setAppliedPromoCode] = useState(null);
  const [promoCodeLoading, setPromoCodeLoading] = useState(false);
  const [promoCodeError, setPromoCodeError] = useState('');
  const [promoInfoDialog, setPromoInfoDialog] = useState(null);
  const [showPromoGiftChoiceModal, setShowPromoGiftChoiceModal] = useState(false);
  const [cardPaymentStage, setCardPaymentStage] = useState('');
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showInactivityPrompt, setShowInactivityPrompt] = useState(false);
  const [inactivityPromptCountdown, setInactivityPromptCountdown] = useState(5);
  const [previewProduct, setPreviewProduct] = useState(null);
  const [previewSupplementIds, setPreviewSupplementIds] = useState([]);
  const [cartFeedback, setCartFeedback] = useState(null);
  const [lastVisibleTicketNumber, setLastVisibleTicketNumber] = useState(null);
  const [showAdminAccessModal, setShowAdminAccessModal] = useState(false);
  const [adminAccessPassword, setAdminAccessPassword] = useState('');
  const [adminAccessError, setAdminAccessError] = useState('');
  const activeCardCheckoutFlowRef = useRef(0);

  const handleTapFeedback = (event) => {
    const target = event.currentTarget;
    if (!target?.classList) {
      return;
    }

    target.classList.remove('is-tapped');
    window.requestAnimationFrame(() => {
      target.classList.add('is-tapped');
      window.setTimeout(() => {
        target.classList.remove('is-tapped');
      }, 320);
    });
  };

  const markKioskOnline = () => {
    setKioskConnectionState('online');
    setKioskConnectionMessage('');
  };

  const markKioskDegraded = (message = DEFAULT_DEGRADED_MESSAGE) => {
    setKioskConnectionState((current) => (current === 'offline' ? 'offline' : 'degraded'));
    setKioskConnectionMessage(message);
  };

  const markKioskOffline = (message = DEFAULT_DEGRADED_MESSAGE) => {
    setKioskConnectionState('offline');
    setKioskConnectionMessage(message);
  };

  const fetchJson = async (url, options = {}, requestOptions = {}) => {
    const {
      timeoutMs = 12000,
      degradedMessage = DEFAULT_DEGRADED_MESSAGE,
    } = requestOptions;
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(url, {
        ...options,
        headers: buildKioskAuthHeaders(options.headers),
        signal: controller.signal,
      });
      const contentType = String(response.headers.get('content-type') || '').toLowerCase();
      const payload = contentType.includes('application/json')
        ? await response.json()
        : await response.text();

      if (!response.ok) {
        const message = typeof payload === 'object' && payload
          ? (payload.error || payload.message || `Erreur HTTP ${response.status}`)
          : `Erreur HTTP ${response.status}`;
        const requestError = new Error(message);
        requestError.isHttpError = true;
        requestError.httpStatus = response.status;
        if (response.status >= 500 || response.status === 0) {
          markKioskDegraded(degradedMessage);
        } else {
          markKioskOnline();
        }
        throw requestError;
      }

      markKioskOnline();
      return payload;
    } catch (error) {
      if (error?.isHttpError) {
        throw error;
      }
      const message = error?.name === 'AbortError'
        ? 'Le serveur ne repond pas assez vite.'
        : (error?.message || degradedMessage);
      markKioskDegraded(message);
      throw new Error(message);
    } finally {
      window.clearTimeout(timeoutId);
    }
  };

  const applyBootstrapPayload = (payload, { fromCache = false, cacheSavedAt = '' } = {}) => {
    const sectionsData = Array.isArray(payload?.sections) ? payload.sections : [];
    const subcategoriesData = Array.isArray(payload?.subcategories) ? payload.subcategories : [];
    const productsData = Array.isArray(payload?.products) ? payload.products : [];
    const menusData = Array.isArray(payload?.menus) ? payload.menus : [];
    const printersData = Array.isArray(payload?.printers) ? payload.printers : [];
    const productionZonesData = Array.isArray(payload?.productionZones) ? payload.productionZones : [];
    const settingsData = payload?.settings || {};

    setSections(
      sectionsData.map((section) => {
        const fullImage = resolveMediaUrl(section?.image);
        return {
          ...section,
          image: fullImage,
          tabCoverImage: getOptimizedCategoryButtonImage(section?.image) || fullImage,
        };
      })
    );
    setSubcategories(
      subcategoriesData.map((subcategory) => {
        const fullImage = resolveMediaUrl(subcategory?.image);
        return {
          ...subcategory,
          image: fullImage,
          buttonCoverImage: getOptimizedSubcategoryButtonImage(subcategory?.image) || fullImage,
          bannerCoverImage: getOptimizedSubcategoryBannerImage(subcategory?.image) || fullImage,
        };
      })
    );
    setProducts(
      productsData
        .filter((product) => product.isBorneAvailable !== false)
        .map((product) => enrichProductMedia(product))
    );
    setMenus(
      menusData.map((menu) => ({
        ...menu,
        image: resolveMediaUrl(menu.image),
        thumbnailImage: getOptimizedProductImage(menu.image) || resolveMediaUrl(menu.image),
        Products: Array.isArray(menu.Products)
          ? menu.Products
              .filter((product) => product.isBorneAvailable !== false && isWithinAvailabilityWindow(product))
              .map((product) => enrichProductMedia(product))
          : [],
      }))
    );
    setPrinters(printersData);
    setProductionZones(productionZonesData);
    setInactivityTimeoutSeconds(settingsData.inactivityTimeoutSeconds || 20);
    setCheckoutSuccessMessage(settingsData.checkoutSuccessMessage || 'Merci pour votre commande.');
    setCheckoutSuccessCashMessage(
      settingsData.checkoutSuccessCashMessage || settingsData.checkoutSuccessMessage || 'Merci pour votre commande.'
    );
    setCheckoutSuccessCardMessage(
      settingsData.checkoutSuccessCardMessage || settingsData.checkoutSuccessMessage || 'Merci pour votre commande.'
    );
    setCheckoutSuccessMessageSize(settingsData.checkoutSuccessMessageSize || 54);
    setCheckoutSuccessMessageDurationSeconds(settingsData.checkoutSuccessMessageDurationSeconds || 2);
    setTicketHeaderText(settingsData.ticketHeaderText || '');
    setTicketHeaderCashText(settingsData.ticketHeaderCashText || settingsData.ticketHeaderText || '');
    setTicketHeaderCardPaidText(
      settingsData.ticketHeaderCardPaidText || settingsData.ticketHeaderText || ''
    );
    setClientTicketZoneId(settingsData.clientTicketZoneId || '');
    setClientTicketCopies(settingsData.clientTicketCopies || 1);
    setTicketLogoMedia(resolveMediaUrl(settingsData.ticketLogoMedia || ''));
    setTicketStoreLine1(settingsData.ticketStoreLine1 || 'MAYTHAI');
    setTicketStoreLine2(settingsData.ticketStoreLine2 || '32 avenue de la Republique');
    setTicketStoreLine3(settingsData.ticketStoreLine3 || '75011 Paris');
    setTicketStoreLine4(settingsData.ticketStoreLine4 || 'TVA : - NAF : 5610A');
    setTicketStoreLine5(settingsData.ticketStoreLine5 || 'Tel: 01 47 00 19 65');
    setTicketStoreLine6(settingsData.ticketStoreLine6 || '');
    setTicketQrText(settingsData.ticketQrText || '');
    setTicketQrUrl(settingsData.ticketQrUrl || '');
    setTicketFooterLine1(settingsData.ticketFooterLine1 || 'Merci de votre visite');
    setTicketFooterLine2(settingsData.ticketFooterLine2 || 'A bientot !');
    setTicketFooterLine3(settingsData.ticketFooterLine3 || '');
    setTicketHeaderTextSize(settingsData.ticketHeaderTextSize || 4);
    setMyposEnabled(!!settingsData.myposEnabled);
    setShowPaymentChoice(false);
    setUiPrimaryColor(settingsData.uiPrimaryColor || '#08383a');
    setUiAccentColor(settingsData.uiAccentColor || '#ccb08c');
    setUiTextColor(settingsData.uiTextColor || '#2b180f');
    setUiBackgroundColor(settingsData.uiBackgroundColor || '#f7f0e4');
    setUiSurfaceColor(settingsData.uiSurfaceColor || 'rgba(255, 250, 243, 0.96)');
    setUiMutedColor(settingsData.uiMutedColor || '#7d6757');
    setUiCategoryTextSize(settingsData.uiCategoryTextSize || 18);
    setUiProductNameTextSize(settingsData.uiProductNameTextSize || 18);
    setUiHeadingTextSize(settingsData.uiHeadingTextSize || 28);
    setUiBodyTextSize(settingsData.uiBodyTextSize || 17);

    const hasDeals = productsData.some((product) => product.isDealOfDay && isWithinAvailabilityWindow(product));
    setActiveTab((current) => current ?? (hasDeals ? DEALS_TAB_ID : sectionsData[0]?.id ?? MENU_TAB_ID));
    setCachedCatalogTimestamp(fromCache ? cacheSavedAt : '');
  };

  const closeConfirmDialog = () => setConfirmDialog(null);
  const continueAfterInactivityPrompt = () => {
    setShowInactivityPrompt(false);
    setInactivityPromptCountdown(5);
  };

  const closeAdminAccessModal = () => {
    setShowAdminAccessModal(false);
    setAdminAccessPassword('');
    setAdminAccessError('');
  };

  const submitAdminAccess = (event) => {
    event.preventDefault();
    if (adminAccessPassword !== ADMIN_MENU_PASSWORD) {
      setAdminAccessError('Mot de passe incorrect.');
      return;
    }
    closeAdminAccessModal();
    navigate('/admin/select');
  };

  const closeProductPreview = () => {
    setPreviewProduct(null);
    setPreviewSupplementIds([]);
  };

  const triggerCartFeedback = (item) => {
    if (cartFeedbackTimeoutRef.current) {
      window.clearTimeout(cartFeedbackTimeoutRef.current);
    }

    setCartFeedback({
      id: `${Date.now()}-${Math.random()}`,
      name: item?.name || 'Produit',
      image: item?.cartImage || item?.thumbnailImage || item?.image || 'https://via.placeholder.com/120x120?text=+1',
    });

    cartFeedbackTimeoutRef.current = window.setTimeout(() => {
      setCartFeedback(null);
    }, 1100);
  };

  const clearCart = () => {
    if (pendingOrderRef.current || uncertainOrderRef.current || checkoutState !== 'idle') return;
    setCart([]);
    setCustomerComment('');
    setIncludeCutlery(false);
    setUseLoyaltyDiscount(false);
    setShowOrderOptions(false);
    setShowPaymentChoice(false);
    setPendingTicketNumber(null);
    rememberPendingOrder(null);
    checkoutRequestIdRef.current = null;
    setSelectedPaymentMethod('');
    setCardPaymentStage('');
    setPromoCodeInput('');
    setAppliedPromoCode(null);
    setPromoCodeError('');
    setPromoInfoDialog(null);
    setShowPromoGiftChoiceModal(false);
  };

  const askConfirmation = ({ title, message, confirmLabel, tone = 'danger', onConfirm }) => {
    setConfirmDialog({
      title,
      message,
      confirmLabel,
      tone,
      onConfirm,
    });
  };

  const handleClearCartRequest = () => {
    if (!cart.length) {
      return;
    }

    askConfirmation({
      title: 'Abandonner le panier',
      message: 'Le panier, les commentaires et la remise fidélité vont être supprimés.',
      confirmLabel: 'Oui, abandonner',
      onConfirm: () => {
        clearCart();
        setShowCart(false);
      },
    });
  };

  const refreshCustomer = async (customerId) => {
    const accountId = customerAccountId;
    const requestId = ++customerRequestRef.current;
    const isCurrent = () => requestId === customerRequestRef.current && accountId === getCustomerSessionAccountId();
    const customer = await refreshCustomerSession({
      customerId, accountId, isCurrent,
      fetchCustomer: async (id) => {
        const payload = await fetchJson(`${API_URL}/api/customers/${id}`, {}, {
          degradedMessage: 'Connexion client indisponible. Le catalogue reste visible, mais certaines fonctions de compte sont limitees.',
        });
        return {
          ...payload,
          recentProducts: Array.isArray(payload.recentProducts)
            ? payload.recentProducts.filter((product) => isWithinAvailabilityWindow(product)).map(enrichRecentProductMedia)
            : [],
        };
      },
    });
    if (!isCurrent()) return null;
    setCurrentCustomer(customer);
    if (!customer) { setUseLoyaltyDiscount(false); setAppliedPromoCode(null); }
    return customer;
  };

  const createOrder = async (ticketNumber) => {
    if (customerAccountId !== getCustomerSessionAccountId()) throw new Error('Le compte de la borne a changé. Actualisez avant de commander.');
    if (!checkoutRequestIdRef.current) {
      checkoutRequestIdRef.current = window.crypto?.randomUUID?.()
        || `kiosk-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      checkoutOrderedAtRef.current = new Date().toISOString();
    }
    const resolveTaxRate = (rawValue, fallback = 10) => {
      const parsed = Number(rawValue);
      if (!Number.isFinite(parsed)) {
        return Number(fallback);
      }
      return Math.max(0, Math.min(100, Number(parsed.toFixed(2))));
    };

    const promoGiftItem = promoGiftCartLine
      ? {
          productId: promoGiftCartLine.productId || null,
          printerId: promoGiftCartLine.printerId || null,
          productionZoneId: promoGiftCartLine.productionZoneId || null,
          name: promoGiftCartLine.name,
          kitchenName: `🎁 ${promoGiftCartLine.kitchenName || promoGiftCartLine.productName || promoGiftCartLine.name}`,
          quantity: 1,
          unitPrice: 0,
          totalPrice: 0,
          taxRate: resolveTaxRate(promoGiftCartLine.taxRate || promoGiftCartLine.Product?.taxRate || 10),
          isMenu: false,
          isPromoGift: true,
          promoCodeId: appliedPromoCode?.id || null,
          selectionsJson: null,
        }
      : null;

    return fetchJson(`${API_URL}/api/orders`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        clientRequestId: checkoutRequestIdRef.current,
        salesChannel: 'borne',
        ticketNumber,
        totalTtc: parseFloat(totalPriceToPay),
        orderedAt: checkoutOrderedAtRef.current,
        customerId: currentCustomer?.id || null,
        customerComment,
        includeCutlery,
        loyaltyPointsUsed: appliedLoyaltyPoints,
        loyaltyDiscountAmount: appliedLoyaltyDiscount,
        promoCodeId: appliedPromoCode?.id || null,
        promoCodeCode: appliedPromoCode?.code || '',
        promoDiscountAmount: appliedPromoDiscount,
        promoRewardType: appliedPromoCode?.rewardType || '',
        customerPhone: currentCustomer?.phone || '',
        items: [
          ...cart.flatMap((item) => {
          if (item.isMenu && Array.isArray(item.customSelections) && item.customSelections.length > 0) {
            return buildMenuPriceLines(item).map(({ selection, product, unitPrice, totalPrice }) => ({
                productId: product?.id || null,
                menuId: item.id,
                printerId: product?.printerId || product?.Printer?.id || null,
                productionZoneId: product?.productionZoneId || product?.ProductionZone?.id || null,
                name: `${item.name} - ${selection.sectionName}: ${product?.name || ''}`,
                kitchenName: `${item.name} - ${selection.sectionName}: ${product?.kitchenName || product?.name || ''}`,
                quantity: item.quantity,
                unitPrice,
                totalPrice,
                taxRate: resolveTaxRate(product?.taxRate ?? product?.Product?.taxRate ?? 10),
                isMenu: true,
                selectionsJson: item.customSelections,
              }));
          }

          return [
            {
              productId: item.id,
              printerId: item.printerId || item.Printer?.id || null,
              productionZoneId: item.productionZoneId || item.ProductionZone?.id || null,
              name: item.name,
              kitchenName: item.kitchenName || item.name,
              quantity: item.quantity,
              unitPrice: item.totalPrice,
              totalPrice: item.totalPrice * item.quantity,
              taxRate: resolveTaxRate(item.taxRate ?? item.Product?.taxRate ?? 10),
              isMenu: false,
              selectionsJson: Array.isArray(item.selectedSupplements) && item.selectedSupplements.length > 0
                ? item.selectedSupplements.map((supplement) => ({
                    kind: 'supplement',
                    id: supplement.id,
                    name: supplement.name,
                    price: Number(supplement.price || 0),
                    image: supplement.image || '',
                    categoryName: supplement.SupplementCategory?.name || '',
              }))
                : null,
            },
          ];
          }),
          ...(promoGiftItem ? [promoGiftItem] : []),
        ],
      }),
    }, {
      degradedMessage: 'Connexion serveur indisponible. La commande ne peut pas etre envoyee pour le moment.',
    });
  };

  const completeCheckoutSuccess = async (mode = 'cash', ticketNumber = pendingTicketNumber) => {
    setLastVisibleTicketNumber(ticketNumber);
    setCart([]);
    setShowCart(false);
    setShowOrderOptions(false);
    setShowPaymentChoice(false);
    setCustomerComment('');
    setIncludeCutlery(false);
    setUseLoyaltyDiscount(false);
    setUpsellProducts([]);
    setPromoCodeInput('');
    setAppliedPromoCode(null);
    setPromoCodeError('');
    setPromoInfoDialog(null);
    setShowPromoGiftChoiceModal(false);
    setCheckoutState('success');
    setCheckoutMessage(
      mode === 'cb'
        ? (checkoutSuccessCardMessage || checkoutSuccessMessage)
        : (checkoutSuccessCashMessage || checkoutSuccessMessage)
    );
    setPendingTicketNumber(null);
    rememberPendingOrder(null);
    checkoutRequestIdRef.current = null;
    setSelectedPaymentMethod('');
    setCardPaymentStage('');
    if (currentCustomer?.id) {
      await refreshCustomer(currentCustomer.id);
    }
  };

  const finalizeCheckout = async ({ skipPrinting = false, autoMarkAsPaid = false } = {}) => {
    setCheckoutState('loading');
    setCardPaymentStage('');
    setCheckoutMessage(skipPrinting ? 'Validation de la commande en cours...' : 'Impression du ticket en cours...');
    let ticketNumber = pendingTicketNumber;

    if (!ticketNumber) {
      const ticketPayload = await fetchJson(`${API_URL}/api/printer-settings/next-ticket-number`, {
        method: 'POST',
      }, {
        degradedMessage: 'Le backend ne repond pas. Impossible de generer un numero de ticket.',
      });
      ticketNumber = ticketPayload.ticketNumber;
      setPendingTicketNumber(ticketNumber);
    }

    const isCashAlreadyPaid = !!autoMarkAsPaid;
    const cashTicketHeaderText = isCashAlreadyPaid
      ? 'DEJA PAYE ESPECE'
      : (ticketHeaderCashText || ticketHeaderText || 'A PAYER ESPECE');

    const order = await ensurePendingOrder(ticketNumber);
    if (autoMarkAsPaid && order?.id && order.paymentStatus !== 'paid') {
      const paidOrder = await fetchJson(`${API_URL}/api/orders/${order.id}/payment`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paymentMethod: 'espece', amountPaid: Number(totalPriceToPay || 0) }),
      }, { degradedMessage: 'Impossible de valider automatiquement le paiement de la commande.' });
      rememberPendingOrder(paidOrder);
    }

    if (!skipPrinting) {
      await printOrderTickets(printers, promoGiftCartLine ? [...cart, promoGiftCartLine] : cart, totalPriceToPay, ticketNumber, {
        ticketHeaderText: cashTicketHeaderText,
        ticketHeaderTextSize,
        ticketLogoMedia,
        ticketStoreLine1,
        ticketStoreLine2,
        ticketStoreLine3,
        ticketStoreLine4,
        ticketStoreLine5,
        ticketStoreLine6,
        ticketQrText,
        ticketQrUrl,
        ticketFooterLine1,
        ticketFooterLine2,
        ticketFooterLine3,
        customerFirstName: currentCustomer?.firstName || '',
        paymentMethod: 'espece',
        isAlreadyPaid: isCashAlreadyPaid,
        customerComment,
        includeCutlery,
        productionZones,
        clientTicketZoneId,
        receiptPrinterId: workstation.device?.receiptPrinterId || null, workstationBound: Boolean(workstation.device),
        clientTicketCopies,
      });
    }

    await completeCheckoutSuccess('cash', ticketNumber);
  };

  const ensurePendingOrder = async (ticketNumber) => {
    if (pendingOrderRef.current?.id) {
      return pendingOrderRef.current;
    }
    if (orderRequestRef.current) return orderRequestRef.current;
    orderRequestRef.current = createOrder(ticketNumber).then((createdOrder) => {
      rememberPendingOrder(createdOrder);
      uncertainOrderRef.current = false;
      return createdOrder;
    }).catch((error) => {
      uncertainOrderRef.current = !error.isHttpError || error.httpStatus >= 500;
      throw error;
    });
    try {
      return await orderRequestRef.current;
    } finally {
      orderRequestRef.current = null;
    }
  };

  const terminalPrintOptions = () => ({
    ticketHeaderText: ticketHeaderCardPaidText || ticketHeaderText, ticketHeaderTextSize,
    ticketLogoMedia, ticketStoreLine1, ticketStoreLine2, ticketStoreLine3, ticketStoreLine4,
    ticketStoreLine5, ticketStoreLine6, ticketQrText, ticketQrUrl,
    ticketFooterLine1, ticketFooterLine2, ticketFooterLine3,
    paymentMethod: 'cb', isAlreadyPaid: true, customerComment, includeCutlery,
    productionZones, clientTicketZoneId, clientTicketCopies, receiptPrinterId: workstation.device?.receiptPrinterId || null, workstationBound: Boolean(workstation.device),
  });

  const finalizeCardCheckout = async () => {
    const scope = getTerminalScope();
    const pending = readPendingTerminalPayment();
    if (pending?.orderId) {
      setTerminalOrderId(pending.orderId);
      setCheckoutState('loading');
      return;
    }
    // Only a successful, explicit disabled response can select the legacy provider.
    const config = await getTerminalConfig(terminalFetch);
    if (getTerminalScope() !== scope) throw new Error('Le compte de la borne a changé. Actualisez avant de payer.');
    setTerminalConfig(config);
    setTerminalConfigError('');
    if (config.enabled === false) {
      if (config.deviceId || workstation.device) throw new Error('Le terminal de ce poste doit être configuré avant le paiement par carte.');
      return finalizeLegacyCardCheckout();
    }
    if (!config.configured) throw new Error('Le terminal de paiement n’est pas configuré. Appelez un membre de l’équipe.');
    const readerId = config.deviceId ? config.assignedReaderId : (config.assignedReaderId || readTerminalReader() || config.defaultReaderId);
    if (!readerId || !config.readers?.some(reader => reader.id === readerId)) throw new Error('Aucun terminal n’est affecté à cette borne. Appelez un membre de l’équipe.');
    if (Math.round(Number(totalPriceToPay) * 100) < 50) throw new Error('Le paiement par carte nécessite au moins 0,50 €. Choisissez les espèces.');
    setCheckoutState('loading');
    setCardPaymentStage('');
    setCheckoutMessage('Préparation du terminal de paiement…');
    let ticketNumber = pendingTicketNumber;
    if (!ticketNumber) {
      const result = await fetchJson(`${API_URL}/api/printer-settings/next-ticket-number`, { method: 'POST' });
      ticketNumber = result.ticketNumber;
      setPendingTicketNumber(ticketNumber);
    }
    const order = await ensurePendingOrder(ticketNumber);
    if (getTerminalScope() !== scope) throw new Error('Le compte de la borne a changé. Actualisez avant de payer.');
    // The snapshot is saved before any terminal action so Android reloads can resume.
    persistPendingTerminalPayment({ orderId: order.id, readerId, state: 'ready', context: {
      source: 'borne', ticketNumber, cart: promoGiftCartLine ? [...cart, promoGiftCartLine] : cart,
      total: Number(totalPriceToPay), printOptions: terminalPrintOptions(), printState: 'pending',
    } });
    setTerminalOrderId(order.id);
  };

  const finishTerminalOrder = async (pending) => {
    clearPendingTerminalPayment(pending.orderId);
    setTerminalOrderId(null);
    setTerminalPrintError('');
    await completeCheckoutSuccess('cb', pending.context?.ticketNumber);
  };

  const printTerminalOrder = async (payload, explicitRetry = false) => {
    const scope = terminalScopeRef.current;
    const pending = readPendingTerminalPayment();
    if (terminalPrintingRef.current) return;
    if (getTerminalScope() !== terminalScopeRef.current || !pending || Number(payload?.orderId) !== Number(pending.orderId)
      || payload.provider !== 'stripe_terminal' || payload.state !== 'paid') throw new Error('Confirmation du paiement indisponible. Appelez un membre de l’équipe.');
    const context = pending.context;
    if (!context || context.source !== 'borne' || !Array.isArray(context.cart) || !context.ticketNumber || !payload.receipt
      || Math.round(Number(context.total) * 100) !== Number(payload.amountCents)) {
      setTerminalPrintError('Le paiement est confirmé, mais le ticket doit être récupéré auprès de l’équipe.');
      return;
    }
    persistPendingTerminalPayment({ ...pending, ...payload, context });
    rememberPendingOrder({ id: pending.orderId, paymentStatus: 'paid' });
    if (!explicitRetry && context.printState !== 'pending') {
      setTerminalPrintError('Paiement confirmé. L’impression précédente doit être vérifiée avant de réimprimer.');
      return;
    }
    terminalPrintingRef.current = true;
    try {
      persistPendingTerminalPayment({ orderId: pending.orderId, context: { ...context, printState: 'printing' } });
      await printOrderTickets(printers, context.cart, context.total, context.ticketNumber, {
        ...context.printOptions, stripeReceipt: payload.receipt,
      });
      if (getTerminalScope() !== scope) return;
      await finishTerminalOrder(pending);
    } catch (printError) {
      if (getTerminalScope() !== scope) return;
      persistPendingTerminalPayment({ orderId: pending.orderId, context: { ...context, printState: 'failed' } });
      setTerminalPrintError(`Paiement confirmé, impression non confirmée : ${printError.message}. Vérifiez le ticket avant de réimprimer.`);
    } finally { terminalPrintingRef.current = false; }
  };

  const cancelTerminalOrder = async (payload) => {
    const scope = getTerminalScope();
    const pending = readPendingTerminalPayment();
    if (!pending || Number(payload?.orderId) !== Number(pending.orderId)
      || !['cancelled', 'ready'].includes(payload.state) || (payload.state === 'ready' && pending.state !== 'ready')) throw new Error('Annulation du paiement non confirmée.');
    await fetchJson(`${API_URL}/api/orders/${pending.orderId}/abandon`, { method: 'POST' });
    if (getTerminalScope() !== scope) return;
    clearPendingTerminalPayment(pending.orderId);
    setTerminalOrderId(null);
    rememberPendingOrder(null);
    setPendingTicketNumber(null);
    checkoutRequestIdRef.current = null;
    uncertainOrderRef.current = false;
    setCheckoutState('idle');
    setSelectedPaymentMethod('');
    setShowCart(true);
  };

  const finalizeLegacyCardCheckout = async () => {
    const flowId = activeCardCheckoutFlowRef.current + 1;
    activeCardCheckoutFlowRef.current = flowId;
    const isFlowActive = () => activeCardCheckoutFlowRef.current === flowId;

    setCheckoutState('loading');
    setCardPaymentStage('waiting_terminal');
    setCheckoutMessage('Cliquez sur le bouton vert du Terminal de Paiement.');
    if (Number(totalPriceToPay || 0) <= 0) {
      throw new Error('Le montant à payer est de 0 €. Valide la commande en espèces (sans encaissement).');
    }
    let ticketNumber = pendingTicketNumber;

    if (!ticketNumber) {
      const ticketPayload = await fetchJson(`${API_URL}/api/printer-settings/next-ticket-number`, {
        method: 'POST',
      }, {
        degradedMessage: 'Le backend ne repond pas. Impossible de generer un numero de ticket.',
      });
      if (!isFlowActive()) return;
      ticketNumber = ticketPayload.ticketNumber;
      setPendingTicketNumber(ticketNumber);
    }

    const order = await ensurePendingOrder(ticketNumber);
    if (!isFlowActive()) {
      return;
    }

    setCardPaymentStage('waiting_terminal');
    setCheckoutMessage('Cliquez sur le bouton vert du Terminal de Paiement.');

    purchaseRequestRef.current = fetchJson(`${API_URL}/api/orders/${order.id}/mypos/purchase`, {
      method: 'POST',
    }, {
      degradedMessage: 'Impossible de joindre le terminal de paiement pour lancer la transaction CB.',
    });
    let purchasePayload;
    try {
      purchasePayload = await purchaseRequestRef.current;
    } finally {
      purchaseRequestRef.current = null;
    }

    let lastOrder = purchasePayload.order || order;
    rememberPendingOrder(lastOrder);
    if (!isFlowActive()) return;

    let attempts = 0;
    while (attempts < 45) {
      attempts += 1;
      await new Promise((resolve) => window.setTimeout(resolve, 2000));
      if (!isFlowActive()) {
        return;
      }

      const statusPayload = await fetchJson(`${API_URL}/api/orders/${order.id}/mypos/status`, {}, {
        degradedMessage: 'Le suivi du paiement CB est indisponible. Verifie le terminal puis reessaie.',
        timeoutMs: 8000,
      });
      if (!isFlowActive()) {
        return;
      }

      lastOrder = statusPayload.order || lastOrder;
      rememberPendingOrder(lastOrder);

      const state = statusPayload.mypos?.state;
      if (state === 'approved') {
        setCardPaymentStage('printing');
        setCheckoutMessage('Paiement validé, impression du ticket...');
        try {
          await printOrderTickets(printers, promoGiftCartLine ? [...cart, promoGiftCartLine] : cart, totalPriceToPay, ticketNumber, {
            ticketHeaderText: ticketHeaderCardPaidText || ticketHeaderText,
            ticketHeaderTextSize,
            ticketLogoMedia,
            ticketStoreLine1,
            ticketStoreLine2,
            ticketStoreLine3,
            ticketStoreLine4,
            ticketStoreLine5,
            ticketStoreLine6,
            ticketQrText,
            ticketQrUrl,
            ticketFooterLine1,
            ticketFooterLine2,
            ticketFooterLine3,
            customerFirstName: currentCustomer?.firstName || '',
            paymentMethod: 'cb',
            isAlreadyPaid: true,
            myposReceipt: buildMyposReceiptFromStatus(statusPayload, totalPriceToPay),
            customerComment,
            includeCutlery,
            productionZones,
            clientTicketZoneId,
            receiptPrinterId: workstation.device?.receiptPrinterId || null, workstationBound: Boolean(workstation.device),
            clientTicketCopies,
          });
        } catch (printError) {
          throw new Error(`Paiement validé, mais impression échouée : ${printError.message}. Réessaie l’impression sans repayer.`);
        }

        if (!isFlowActive()) {
          return;
        }
        await completeCheckoutSuccess('cb', ticketNumber);
        return;
      }

      if (state === 'failed' || state === 'cancelled' || state === 'error') {
        if (!isFlowActive() && state === 'cancelled') {
          return;
        }
        throw new Error(statusPayload.mypos?.statusMessage || 'Paiement refuse sur le terminal.');
      }
    }

    throw new Error('Le terminal n’a pas répondu à temps. Verifie le TPE puis reessaie.');
  };

  const cancelCardCheckout = async () => {
    activeCardCheckoutFlowRef.current += 1;
    setCheckoutState('loading');
    setCardPaymentStage('cancelling');
    setCheckoutMessage('Annulation du paiement en cours...');

    const restoreOrderScreen = () => {
      setCheckoutState('idle');
      setCheckoutMessage('');
      setSelectedPaymentMethod('');
      setCardPaymentStage('');
      setShowPaymentChoice(false);
      setShowCart(true);
    };

    try {
      // Wait for in-flight writes before allowing the basket to change.
      if (orderRequestRef.current) await orderRequestRef.current.catch(() => null);
      if (purchaseRequestRef.current) await purchaseRequestRef.current.catch(() => null);
      if (uncertainOrderRef.current && !pendingOrderRef.current?.id) {
        await ensurePendingOrder(pendingTicketNumber);
      }
      let order = pendingOrderRef.current;
      if (order?.myposStatus === 'pending') {
        const payload = await fetchJson(`${API_URL}/api/orders/${order.id}/mypos/cancel`, {
          method: 'POST',
        }, { degradedMessage: 'Annulation du paiement non confirmée. Le panier reste protégé.', timeoutMs: 8000 });
        order = payload.order || order;
        rememberPendingOrder(order);
      }
      if (order?.id) {
        await fetchJson(`${API_URL}/api/orders/${order.id}/abandon`, { method: 'POST' }, {
          degradedMessage: 'Annulation de la commande non confirmée. Réessaie avant de modifier le panier.',
        });
      }
      rememberPendingOrder(null);
      setPendingTicketNumber(null);
      checkoutRequestIdRef.current = null;
      uncertainOrderRef.current = false;
      restoreOrderScreen();
    } catch (error) {
      setCheckoutState('error');
      setCheckoutMessage(error.message || 'Annulation non confirmée. Le panier reste protégé.');
    }
  };

  useEffect(() => {
    setCurrentCustomer(null);
    setUseLoyaltyDiscount(false);
    setAppliedPromoCode(null);
    const storedCustomer = readCustomerSession(customerAccountId);
    if (storedCustomer?.id) refreshCustomer(storedCustomer.id);
    return () => { customerRequestRef.current += 1; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerAccountId]);

  useEffect(() => {
    const handleOffline = () => {
      markKioskOffline(
        'Connexion perdue. La borne reste consultable avec le catalogue charge, mais l envoi de commande est suspendu.'
      );
    };
    const handleOnline = () => {
      markKioskDegraded('Connexion retablie. Reconnexion au serveur...');
      setBootstrapReloadToken((current) => current + 1);
    };

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => () => {
    if (cartFeedbackTimeoutRef.current) {
      window.clearTimeout(cartFeedbackTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        if (isMounted) {
          setLoading(true);
          setError('');
        }

        const [sectionsData, subcategoriesData, productsData, menusData, printersData, productionZonesData, settingsData] = await Promise.all([
          fetchJson(`${API_URL}/api/sections`, {}, { degradedMessage: DEFAULT_DEGRADED_MESSAGE }),
          fetchJson(`${API_URL}/api/subcategories`, {}, { degradedMessage: DEFAULT_DEGRADED_MESSAGE }),
          fetchJson(`${API_URL}/api/products`, {}, { degradedMessage: DEFAULT_DEGRADED_MESSAGE }),
          fetchJson(`${API_URL}/api/menus`, {}, { degradedMessage: DEFAULT_DEGRADED_MESSAGE }),
          fetchJson(`${API_URL}/api/printers`, {}, { degradedMessage: DEFAULT_DEGRADED_MESSAGE }),
          fetchJson(`${API_URL}/api/production-zones`, {}, { degradedMessage: DEFAULT_DEGRADED_MESSAGE }),
          fetchJson(`${API_URL}/api/printer-settings`, {}, { degradedMessage: DEFAULT_DEGRADED_MESSAGE }),
        ]);

        if (!isMounted) {
          return;
        }

        const payload = {
          sections: sectionsData,
          subcategories: subcategoriesData,
          products: productsData,
          menus: menusData,
          printers: printersData,
          productionZones: productionZonesData,
          settings: settingsData,
        };
        applyBootstrapPayload(payload);
        saveBootstrapCache(payload);
        markKioskOnline();
      } catch (fetchError) {
        console.error('Erreur lors de la récupération des données:', fetchError);
        if (isMounted) {
          const cached = getStoredBootstrapCache();
          if (cached?.payload) {
            applyBootstrapPayload(cached.payload, {
              fromCache: true,
              cacheSavedAt: cached.savedAt || '',
            });
            markKioskDegraded(
              'Mode degrade actif: le dernier catalogue connu reste affiche, mais le paiement et l envoi de commande sont bloques.'
            );
            setError('');
          } else {
            setError(`Impossible de charger la borne. Vérifie que le backend tourne sur ${API_URL}.`);
          }
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    // Keep kiosk interactions smooth: no periodic full refresh while the client is ordering.
    fetchData();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrapReloadToken]);

  useEffect(() => {
    setActiveSubcategoryId('all');
  }, [activeTab]);

  useEffect(() => {
    if (checkoutState !== 'success') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setCheckoutState('idle');
      setCheckoutMessage('');
      navigate('/veille');
    }, Math.max(1, Number(checkoutSuccessMessageDurationSeconds) || 2) * 1000);

    return () => window.clearTimeout(timeoutId);
  }, [checkoutState, checkoutSuccessMessageDurationSeconds, navigate]);

  useEffect(() => {
    if (checkoutState !== 'idle') {
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }
      if (showInactivityPrompt) {
        setShowInactivityPrompt(false);
        setInactivityPromptCountdown(5);
      }
      return undefined;
    }

    const resetInactivityTimer = () => {
      if (showInactivityPrompt) {
        return;
      }

      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }

      inactivityTimerRef.current = window.setTimeout(() => {
        setInactivityPromptCountdown(5);
        setShowInactivityPrompt(true);
      }, Math.max(5, Number(inactivityTimeoutSeconds) || 20) * 1000);
    };

    const events = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'click', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, resetInactivityTimer));
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [checkoutState, inactivityTimeoutSeconds, navigate, showInactivityPrompt]);

  useEffect(() => {
    if (!showInactivityPrompt || checkoutState !== 'idle') {
      return undefined;
    }

    if (inactivityPromptCountdown <= 0) {
      navigate('/veille');
      return undefined;
    }

    const countdownId = window.setTimeout(() => {
      setInactivityPromptCountdown((prev) => prev - 1);
    }, 1000);

    return () => {
      window.clearTimeout(countdownId);
    };
  }, [showInactivityPrompt, inactivityPromptCountdown, checkoutState, navigate]);

  useEffect(() => {
    if (checkoutState !== 'idle' || showPaymentChoice) {
      setShowCart(false);
      setShowOrderOptions(false);
    }
  }, [checkoutState, showPaymentChoice]);

  const buildCartKey = (item, isMenu = false) => {
    if (isMenu) {
      const menuSelectionKey = Array.isArray(item.customSelections)
        ? item.customSelections
            .map((selection) => `${selection.sectionName}:${selection.product?.id || 'x'}`)
            .sort()
            .join('|')
        : 'default';
      return `menu-${item.id}-${menuSelectionKey}`;
    }

    const supplementKey = Array.isArray(item.selectedSupplements)
      ? item.selectedSupplements
          .map((supplement) => supplement.id)
          .sort((a, b) => a - b)
          .join('|') || 'base'
      : 'base';

    return `product-${item.id}-${supplementKey}`;
  };

  const addToCart = (item, isMenu = false) => {
    if (checkoutState !== 'idle' || pendingOrderRef.current || uncertainOrderRef.current) return;
    const additionalPrice = isMenu
      ? Array.isArray(item.customSelections)
        ? Number(item.additionalPrice || 0)
        : item.Products?.reduce(
            (sum, product) => sum + (product.MenuProducts?.additionalPrice || 0),
            0
          )
      : (item.selectedSupplements || []).reduce((sum, supplement) => sum + (Number(supplement.price) || 0), 0);
    const cartKey = buildCartKey(item, isMenu);

    setCart((prevCart) => {
      const existingItem = prevCart.find(
        (cartItem) => cartItem.cartKey === cartKey
      );

      if (existingItem) {
        return prevCart.map((cartItem) =>
          cartItem.cartKey === cartKey
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      }

      return [
        ...prevCart,
        {
          ...item,
          cartKey,
          isMenu,
          totalPrice: parseFloat(item.price) + additionalPrice,
          additionalPrice,
          quantity: 1,
        },
      ];
    });

    triggerCartFeedback(item);
  };

  const removeFromCart = (cartKey) => {
    if (checkoutState !== 'idle' || pendingOrderRef.current || uncertainOrderRef.current) return;
    setCart((prevCart) => prevCart.filter((item) => item.cartKey !== cartKey));
  };

  const updateQuantity = (cartKey, delta) => {
    if (checkoutState !== 'idle' || pendingOrderRef.current || uncertainOrderRef.current) return;
    setCart((prevCart) =>
      prevCart
        .map((item) =>
          item.cartKey === cartKey
            ? { ...item, quantity: item.quantity + delta }
            : item
        )
        .filter((item) => item.quantity > 0)
    );
  };

  const requestQuantityChange = (item, delta) => {
    if (delta < 0 && item.quantity === 1 && totalItemsInCart >= 4) {
      askConfirmation({
        title: 'Supprimer cet article',
        message: `${item.name} sera retiré du panier.`,
        confirmLabel: 'Oui, supprimer',
        onConfirm: () => updateQuantity(item.cartKey, delta),
      });
      return;
    }

    updateQuantity(item.cartKey, delta);
  };

  const closeMenuCustomization = () => {
    setSelectedMenu(null);
    setMenuSelections({});
    setMenuSelectionError('');
  };

  const openMenuCustomization = (menu) => {
    setSelectedMenu(menu);
    setMenuSelections({});
    setMenuSelectionError('');
  };

  const handleMenuSelection = (section, product) => {
    const requiredQuantity = getSectionRequiredQuantity(section);
    const currentSelections = getMenuSectionSelections(menuSelections, section.id);
    const isAlreadySelected = currentSelections.some((selectedProduct) => selectedProduct.id === product.id);

    if (!isAlreadySelected && currentSelections.length >= requiredQuantity) {
      setMenuSelectionError(`Tu as déjà choisi ${requiredQuantity} ${section.name.toLowerCase()}${requiredQuantity > 1 ? 's' : ''}.`);
      return;
    }

    setMenuSelectionError('');
    setMenuSelections((prev) => {
      const selections = getMenuSectionSelections(prev, section.id);

      if (selections.some((selectedProduct) => selectedProduct.id === product.id)) {
        return {
          ...prev,
          [section.id]: selections.filter((selectedProduct) => selectedProduct.id !== product.id),
        };
      }

      return {
        ...prev,
        [section.id]: [...selections, product],
      };
    });
  };

  const addCustomMenuToCart = () => {
    if (!selectedMenu) return;

    if (!isWithinAvailabilityWindow(selectedMenu)) {
      setMenuSelectionError('Ce menu n’est plus disponible pour le moment.');
      return;
    }

    const missingSection = visibleMenuSections.find((section) => {
      const selectedCount = getMenuSectionSelections(menuSelections, section.id).length;
      return selectedCount < getSectionRequiredQuantity(section);
    });

    if (missingSection) {
      setMenuSelectionError(getMenuSelectionShortageMessage(missingSection, getMenuSectionSelections(menuSelections, missingSection.id).length));
      return;
    }

    const additionalPrice = visibleMenuSections.reduce((sum, section) => {
      const selectedProducts = getMenuSectionSelections(menuSelections, section.id);
      return sum + selectedProducts.reduce(
        (sectionSum, product) => sectionSum + Number(product.MenuProducts?.additionalPrice || 0),
        0
      );
    }, 0);

    const customSelections = selectedMenu.Sections.map((section) => ({
      sectionId: section.id,
      sectionName: section.name,
      products: getMenuSectionSelections(menuSelections, section.id),
    }));

    const customMenu = {
      ...selectedMenu,
      customSelections,
      totalPrice: parseFloat(selectedMenu.price) + additionalPrice,
      additionalPrice,
      quantity: 1,
    };

    addToCart(customMenu, true);
    closeMenuCustomization();
  };

  const openProductSupplementModal = (product) => {
    const availableSupplements = Array.isArray(product.Supplements) ? product.Supplements : [];

    if (!availableSupplements.length) {
      addToCart(product);
      return;
    }

    setSupplementProduct(product);
    setSelectedSupplementIds([]);
  };

  const openProductPreview = (product) => {
    const detailImage = getOptimizedProductDetailImage(product?.image);
    setPreviewProduct({
      ...product,
      detailImage: detailImage || product?.detailImage || product?.image || '',
    });
    setPreviewSupplementIds([]);
  };

  const toggleSupplementSelection = (supplementId) => {
    setSelectedSupplementIds((prev) =>
      prev.includes(supplementId)
        ? prev.filter((id) => id !== supplementId)
        : [...prev, supplementId]
    );
  };

  const confirmProductWithSupplements = () => {
    if (!supplementProduct) {
      return;
    }

    const selectedSupplements = (supplementProduct.Supplements || []).filter((supplement) =>
      selectedSupplementIds.includes(supplement.id)
    );

    addToCart({ ...supplementProduct, selectedSupplements });
    setSupplementProduct(null);
    setSelectedSupplementIds([]);
  };

  const togglePreviewSupplementSelection = (supplementId) => {
    setPreviewSupplementIds((prev) =>
      prev.includes(supplementId)
        ? prev.filter((id) => id !== supplementId)
        : [...prev, supplementId]
    );
  };

  const confirmPreviewProductAdd = () => {
    if (!previewProduct) {
      return;
    }

    const selectedSupplements = (previewProduct.Supplements || []).filter((supplement) =>
      previewSupplementIds.includes(supplement.id)
    );

    addToCart({ ...previewProduct, selectedSupplements });
    closeProductPreview();
  };

  const activeSection = useMemo(
    () => sections.find((section) => section.id === activeTab) || null,
    [sections, activeTab]
  );

  const sectionSubcategories = useMemo(
    () => subcategories
      .filter((subcategory) => String(subcategory.sectionId) === String(activeSection?.id))
      .sort((a, b) => {
        const aOrder = parseInt(a?.displayOrder ?? 0, 10) || 0;
        const bOrder = parseInt(b?.displayOrder ?? 0, 10) || 0;
        if (aOrder !== bOrder) {
          return aOrder - bOrder;
        }
        return String(a?.name || '').localeCompare(String(b?.name || ''), 'fr', { sensitivity: 'base' });
      }),
    [subcategories, activeSection]
  );

  const filteredProducts = useMemo(() => {
    if (activeTab === DEALS_TAB_ID) {
      return products.filter((product) => product.isDealOfDay && isWithinAvailabilityWindow(product));
    }

    const productsForSection = products.filter(
      (product) => String(product.sectionId) === String(activeSection?.id) && isWithinAvailabilityWindow(product)
    );

    if (activeSubcategoryId === 'all') {
      return productsForSection;
    }

    return productsForSection.filter(
      (product) => String(product.subcategoryId) === String(activeSubcategoryId)
    );
  }, [products, activeSection, activeSubcategoryId, activeTab]);

  const filteredProductRows = useMemo(() => {
    if (activeTab === MENU_TAB_ID || activeTab === DEALS_TAB_ID || activeSubcategoryId !== 'all') {
      return filteredProducts.map((product) => ({ type: 'product', id: `product-${product.id}`, product }));
    }

    const knownSubcategoryMap = new Map(sectionSubcategories.map((subcategory) => [String(subcategory.id), subcategory]));
    const groupedProducts = new Map();
    const orphanProducts = [];

    for (const product of filteredProducts) {
      const subcategoryKey = String(product?.subcategoryId || '').trim();
      if (subcategoryKey && knownSubcategoryMap.has(subcategoryKey)) {
        if (!groupedProducts.has(subcategoryKey)) {
          groupedProducts.set(subcategoryKey, []);
        }
        groupedProducts.get(subcategoryKey).push(product);
      } else {
        orphanProducts.push(product);
      }
    }

    const rows = [];
    for (const subcategory of sectionSubcategories) {
      const productsForSubcategory = groupedProducts.get(String(subcategory.id)) || [];
      if (!productsForSubcategory.length) {
        continue;
      }

      rows.push({
        type: 'banner',
        id: `subcategory-banner-${subcategory.id}`,
        subcategory,
      });

      for (const product of productsForSubcategory) {
        rows.push({ type: 'product', id: `product-${product.id}`, product });
      }
    }

    if (orphanProducts.length) {
      if (!sectionSubcategories.length) {
        for (const product of orphanProducts) {
          rows.push({ type: 'product', id: `product-${product.id}`, product });
        }
        return rows;
      }

      rows.push({
        type: 'banner',
        id: 'subcategory-banner-orphan',
        subcategory: {
          id: 'orphan',
          name: 'Autres produits',
          bannerCoverImage: '',
        },
      });
      for (const product of orphanProducts) {
        rows.push({ type: 'product', id: `product-${product.id}`, product });
      }
    }

    return rows;
  }, [activeSubcategoryId, activeTab, filteredProducts, sectionSubcategories]);

  const previewSupplementGroups = useMemo(() => {
    if (!previewProduct) {
      return [];
    }

    return Object.values(
      (previewProduct.Supplements || []).reduce((groups, supplement) => {
        const key = supplement.SupplementCategory?.name || 'Suppléments';
        if (!groups[key]) {
          groups[key] = { name: key, items: [] };
        }
        groups[key].items.push(supplement);
        return groups;
      }, {})
    );
  }, [previewProduct]);

  const previewSelectedSupplements = useMemo(() => {
    if (!previewProduct) {
      return [];
    }

    return (previewProduct.Supplements || []).filter((supplement) => previewSupplementIds.includes(supplement.id));
  }, [previewProduct, previewSupplementIds]);

  const previewTotalPrice = useMemo(() => {
    if (!previewProduct) {
      return 0;
    }

    const supplementsPrice = previewSelectedSupplements.reduce(
      (sum, supplement) => sum + Number(supplement.price || 0),
      0
    );

    return Number(previewProduct.price || 0) + supplementsPrice;
  }, [previewProduct, previewSelectedSupplements]);

  const totalItemsInCart = cart.reduce((sum, item) => sum + item.quantity, 0);
  const totalPriceInCart = cart
    .reduce((sum, item) => sum + item.totalPrice * item.quantity, 0)
    .toFixed(2);
  const totalPriceInCartValue = Number(totalPriceInCart || 0);
  const currentPoints = Number(currentCustomer?.points || 0);
  const possibleDiscountEuros = Math.floor(currentPoints / 30);
  const possibleCartDiscount = Math.min(possibleDiscountEuros, Math.floor(totalPriceInCartValue || 0));
  const appliedLoyaltyDiscount = useLoyaltyDiscount ? possibleCartDiscount : 0;
  const appliedLoyaltyPoints = appliedLoyaltyDiscount * 30;
  const totalAfterLoyaltyDiscount = Math.max(0, totalPriceInCartValue - appliedLoyaltyDiscount);
  const promoGiftResolution = useMemo(() => {
    const baseResolution = {
      cartGiftDiscountByKey: new Map(),
      discountAmount: 0,
      extraGiftProduct: null,
      requiresChoice: false,
    };

    if (!appliedPromoCode) {
      return baseResolution;
    }

    if (appliedPromoCode.rewardType === 'product') {
      const offeredProduct = appliedPromoCode.product || null;
      if (!offeredProduct) {
        return baseResolution;
      }

      const matchingCartItems = cart
        .filter((item) => !item.isMenu && String(item.id || '') === String(offeredProduct.id || ''));
      if (!matchingCartItems.length) {
        return {
          ...baseResolution,
          extraGiftProduct: offeredProduct,
        };
      }

      const giftedItem = matchingCartItems.reduce((currentCheapest, item) => {
        if (!currentCheapest) {
          return item;
        }
        return Number(item.totalPrice || 0) < Number(currentCheapest.totalPrice || 0) ? item : currentCheapest;
      }, null);
      const giftDiscount = Number(Math.max(0, Number(giftedItem?.totalPrice || offeredProduct.price || 0)).toFixed(2));
      const cartGiftDiscountByKey = new Map();
      if (giftedItem?.cartKey) {
        cartGiftDiscountByKey.set(giftedItem.cartKey, giftDiscount);
      }
      return {
        cartGiftDiscountByKey,
        discountAmount: giftDiscount,
        extraGiftProduct: null,
        requiresChoice: false,
      };
    }

    if (appliedPromoCode.rewardType === 'product_choice') {
      const eligibleProducts = Array.isArray(appliedPromoCode.eligibleProducts)
        ? appliedPromoCode.eligibleProducts
        : [];
      const eligibleById = new Map(eligibleProducts.map((product) => [String(product.id || ''), product]));
      const selectedGiftProductId = String(appliedPromoCode.selectedGiftProductId || '').trim();

      if (selectedGiftProductId && eligibleById.has(selectedGiftProductId)) {
        return {
          ...baseResolution,
          extraGiftProduct: eligibleById.get(selectedGiftProductId) || null,
        };
      }

      const eligibleCartItems = cart.filter((item) => !item.isMenu && eligibleById.has(String(item.id || '')));
      if (!eligibleCartItems.length) {
        return {
          ...baseResolution,
          requiresChoice: true,
        };
      }

      const giftedItem = eligibleCartItems.reduce((currentCheapest, item) => {
        if (!currentCheapest) {
          return item;
        }
        return Number(item.totalPrice || 0) < Number(currentCheapest.totalPrice || 0) ? item : currentCheapest;
      }, null);
      const giftDiscount = Number(Math.max(0, Number(giftedItem?.totalPrice || 0)).toFixed(2));
      const cartGiftDiscountByKey = new Map();
      if (giftedItem?.cartKey) {
        cartGiftDiscountByKey.set(giftedItem.cartKey, giftDiscount);
      }
      return {
        cartGiftDiscountByKey,
        discountAmount: giftDiscount,
        extraGiftProduct: null,
        requiresChoice: false,
      };
    }

    return baseResolution;
  }, [appliedPromoCode, cart]);
  const appliedPromoDiscount = useMemo(() => {
    if (!appliedPromoCode) {
      return 0;
    }

    if (appliedPromoCode.rewardType === 'product' || appliedPromoCode.rewardType === 'product_choice') {
      return Number(Math.max(0, promoGiftResolution.discountAmount || 0).toFixed(2));
    }

    if (appliedPromoCode.rewardType === 'discount_percent') {
      return Math.max(
        0,
        Math.min(
          totalAfterLoyaltyDiscount,
          totalAfterLoyaltyDiscount * (Number(appliedPromoCode.rewardValue || 0) / 100)
        )
      );
    }

    return Math.max(0, Math.min(totalAfterLoyaltyDiscount, Number(appliedPromoCode.rewardValue || 0)));
  }, [appliedPromoCode, promoGiftResolution.discountAmount, totalAfterLoyaltyDiscount]);
  const promoGiftCartLine = promoGiftResolution.extraGiftProduct
    ? {
        id: `promo-${appliedPromoCode?.id || 'gift'}`,
        productId: promoGiftResolution.extraGiftProduct.id,
        name: `🎁 ${promoGiftResolution.extraGiftProduct.name}`,
        image: promoGiftResolution.extraGiftProduct.image || '',
        thumbnailImage:
          promoGiftResolution.extraGiftProduct.thumbnailImage
          || getOptimizedCartImage(promoGiftResolution.extraGiftProduct.image || ''),
        cartImage:
          promoGiftResolution.extraGiftProduct.cartImage
          || getOptimizedCartImage(promoGiftResolution.extraGiftProduct.image || ''),
        printerId:
          promoGiftResolution.extraGiftProduct.printerId
          || promoGiftResolution.extraGiftProduct.Printer?.id
          || null,
        productionZoneId:
          promoGiftResolution.extraGiftProduct.productionZoneId
          || promoGiftResolution.extraGiftProduct.ProductionZone?.id
          || null,
        kitchenName: promoGiftResolution.extraGiftProduct.kitchenName || promoGiftResolution.extraGiftProduct.name,
        totalPrice: 0,
        quantity: 1,
        isPromoGift: true,
        productName: promoGiftResolution.extraGiftProduct.name,
      }
    : null;
  const promoGiftChoiceRequired = appliedPromoCode?.rewardType === 'product_choice'
    && promoGiftResolution.requiresChoice;
  const totalPriceToPay = Math.max(
    0,
    totalAfterLoyaltyDiscount - appliedPromoDiscount
  ).toFixed(2);

  const cartSectionNames = useMemo(() => {
    const names = new Set();

    cart.forEach((item) => {
      if (item.isMenu && Array.isArray(item.customSelections)) {
        item.customSelections.forEach((selection) => {
          names.add(getCanonicalSectionName(selection.sectionName));
        });
        return;
      }

      names.add(getCanonicalSectionName(item.Section?.name));
    });

    return names;
  }, [cart]);

  const suggestedUpsellProducts = useMemo(() => {
    const hasDessert = cartSectionNames.has('Dessert');
    const hasBoisson = cartSectionNames.has('Boisson');
    let targetSections = [];

    if (!hasDessert && !hasBoisson) {
      targetSections = ['Boisson', 'Dessert', 'Side'];
    } else if (!hasBoisson) {
      targetSections = ['Boisson', 'Side'];
    } else if (!hasDessert) {
      targetSections = ['Dessert', 'Side'];
    } else {
      targetSections = ['Side'];
    }

    return products.filter((product) => {
      const sectionName = getCanonicalSectionName(product.Section?.name);
      return targetSections.includes(sectionName) && isWithinAvailabilityWindow(product);
    });
  }, [cartSectionNames, products]);

  const recentProducts = useMemo(
    () => (Array.isArray(currentCustomer?.recentProducts) ? currentCustomer.recentProducts.filter((product) => isWithinAvailabilityWindow(product)) : []),
    [currentCustomer]
  );

  const availableMenus = useMemo(
    () => menus.filter((menu) => isWithinAvailabilityWindow(menu)),
    [menus]
  );

  const visibleMenuSections = useMemo(
    () =>
      selectedMenu
        ? selectedMenu.Sections.map((section) => ({
            ...section,
            requiredQuantity: getSectionRequiredQuantity(section),
            products: (selectedMenu.Products || []).filter(
              (product) => product.sectionId === section.id && isWithinAvailabilityWindow(product)
            ),
          }))
        : [],
    [selectedMenu]
  );

  const closePromoInfoDialog = () => setPromoInfoDialog(null);

  const cancelPromoGiftChoice = () => {
    setShowPromoGiftChoiceModal(false);
    setAppliedPromoCode(null);
    setPromoCodeError('Choix du cadeau annulé.');
  };

  const handleChoosePromoGift = (product) => {
    if (!product) {
      return;
    }

    setAppliedPromoCode((currentPromo) => {
      if (!currentPromo) {
        return currentPromo;
      }
      return {
        ...currentPromo,
        selectedGiftProductId: product.id,
      };
    });
    setPromoCodeError('');
    setShowPromoGiftChoiceModal(false);
    setPromoInfoDialog({
      title: 'Cadeau validé',
      message: `${product.name} est ajouté en cadeau dans ton panier.`,
    });
  };

  const applyPromoCode = async () => {
    const code = String(promoCodeInput || '').replace(/\D/g, '').trim();
    if (!code) {
      setPromoCodeError('Entre un code promo.');
      return;
    }

    if (!currentCustomer?.phone) {
      setPromoCodeError('Connecte-toi avec ton numéro de téléphone pour utiliser un code promo.');
      return;
    }

    setPromoCodeLoading(true);
    setPromoCodeError('');
    setPromoInfoDialog(null);
    setShowPromoGiftChoiceModal(false);

    try {
      const payload = await fetchJson(`${API_URL}/api/promo-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, phone: currentCustomer.phone }),
      }, {
        degradedMessage: 'Le serveur promo est indisponible. Reessaie quand la connexion est revenue.',
      });

      const normalizedPromo = {
        ...payload,
        product: payload.product ? enrichProductMedia(payload.product) : null,
        eligibleProducts: Array.isArray(payload.eligibleProducts)
          ? payload.eligibleProducts.map((product) => enrichProductMedia(product))
          : [],
        selectedGiftProductId: null,
      };
      setAppliedPromoCode(normalizedPromo);

      if (normalizedPromo.rewardType === 'product' && normalizedPromo.product) {
        const matchingCartItems = cart.filter(
          (item) => !item.isMenu && String(item.id || '') === String(normalizedPromo.product.id || '')
        );
        if (matchingCartItems.length > 0) {
          setPromoInfoDialog({
            title: 'Cadeau appliqué',
            message: `${normalizedPromo.product.name} est déjà dans le panier: un article passe en offert.`,
          });
        } else {
          setPromoInfoDialog({
            title: 'Cadeau ajouté',
            message: `${normalizedPromo.product.name} est ajouté gratuitement dans ton panier.`,
          });
        }
        return;
      }

      if (normalizedPromo.rewardType === 'product_choice') {
        const eligibleById = new Map(
          (Array.isArray(normalizedPromo.eligibleProducts) ? normalizedPromo.eligibleProducts : [])
            .map((product) => [String(product.id || ''), product])
        );
        const eligibleCartItems = cart.filter((item) => !item.isMenu && eligibleById.has(String(item.id || '')));

        if (!eligibleById.size) {
          setAppliedPromoCode(null);
          setPromoCodeError('Ce code ne contient aucun produit éligible.');
          return;
        }

        if (eligibleCartItems.length > 0) {
          const cheapestItem = eligibleCartItems.reduce((currentCheapest, item) => {
            if (!currentCheapest) {
              return item;
            }
            return Number(item.totalPrice || 0) < Number(currentCheapest.totalPrice || 0) ? item : currentCheapest;
          }, null);
          setPromoInfoDialog({
            title: 'Cadeau appliqué',
            message: `${cheapestItem?.name || 'Le produit le moins cher'} passe en offert dans ton panier.`,
          });
          return;
        }

        setShowPromoGiftChoiceModal(true);
        return;
      }

      const rewardLabel = normalizedPromo.rewardType === 'discount_percent'
        ? `${Number(normalizedPromo.rewardValue || 0)} %`
        : formatEuro(normalizedPromo.rewardValue || 0);
      const rawDiscount = normalizedPromo.rewardType === 'discount_percent'
        ? totalAfterLoyaltyDiscount * (Number(normalizedPromo.rewardValue || 0) / 100)
        : Number(normalizedPromo.rewardValue || 0);
      const appliedDiscount = Math.max(0, Math.min(totalAfterLoyaltyDiscount, rawDiscount));
      const totalAfterPromo = Math.max(0, totalAfterLoyaltyDiscount - appliedDiscount);
      const wasClampedToZero = totalAfterLoyaltyDiscount - rawDiscount < 0;

      setPromoInfoDialog({
        title: 'Remise appliquée',
        message: wasClampedToZero
          ? `Bon ${rewardLabel} appliqué. Économie: ${formatEuro(appliedDiscount)}. Le total est ramené à 0.00 € (jamais négatif).`
          : `Bon ${rewardLabel} appliqué. Économie: ${formatEuro(appliedDiscount)}. Total à payer: ${formatEuro(totalAfterPromo)}.`,
      });
    } catch (error) {
      setAppliedPromoCode(null);
      setShowPromoGiftChoiceModal(false);
      setPromoCodeError(error.message);
    } finally {
      setPromoCodeLoading(false);
    }
  };

  const continueCheckoutFlow = async () => {
    if (promoGiftChoiceRequired) {
      setPromoCodeError('Choisis ton cadeau avant de continuer.');
      setShowPromoGiftChoiceModal(true);
      return;
    }

    if (Number(totalPriceToPay || 0) <= 0) {
      setShowPaymentChoice(false);
      setSelectedPaymentMethod('espece');
      await finalizeCheckout({ autoMarkAsPaid: true });
      return;
    }

    setShowPaymentChoice(true);
  };

  const handleCheckout = async () => {
    if (!cart.length || checkoutState !== 'idle') return;

    try {
      if (workstation.loading || workstation.error || workstation.bindingRequired) throw new Error('Cette borne doit être associée à son poste par l’équipe avant de commander.');
      if (kioskConnectionState !== 'online') {
        throw new Error('Le serveur est indisponible. La consultation reste possible, mais le paiement est bloque.');
      }
      setShowCart(false);
      if (promoGiftChoiceRequired || Number(totalPriceToPay || 0) <= 0) {
        await continueCheckoutFlow();
        return;
      }
      if (suggestedUpsellProducts.length > 0 && Number(totalPriceToPay || 0) > 0) {
        setUpsellProducts(suggestedUpsellProducts);
        return;
      }
      await continueCheckoutFlow();
    } catch (err) {
      console.error(err);
      setCheckoutState('error');
      setCheckoutMessage(err.message || 'Impossible d’imprimer le ticket.');
    }
  };

  const retryCheckout = async () => {
    try {
      if (selectedPaymentMethod === 'cb') {
        await finalizeCardCheckout();
      } else {
        await finalizeCheckout({ autoMarkAsPaid: Number(totalPriceToPay || 0) <= 0 });
      }
    } catch (err) {
      console.error(err);
      setCheckoutState('error');
      setCheckoutMessage(err.message || 'Impossible d’imprimer le ticket.');
    }
  };

  const validateWithoutTicket = async () => {
    try {
      await finalizeCheckout({
        skipPrinting: true,
        autoMarkAsPaid: Number(totalPriceToPay || 0) <= 0,
      });
    } catch (err) {
      console.error(err);
      setCheckoutState('error');
      setCheckoutMessage(err.message || 'Impossible de valider la commande.');
    }
  };

  const canDisplayCartPanel = checkoutState === 'idle' && !showPaymentChoice;
  const canDisplayFloatingCart =
    checkoutState === 'idle'
    && !showPaymentChoice
    && !selectedMenu
    && !supplementProduct
    && !upsellProducts.length
    && !showOrderOptions;
  const isCheckoutBlocked = kioskConnectionState !== 'online';
  const showConnectionBanner = !error && (kioskConnectionState === 'offline' || kioskConnectionState === 'degraded');

  return (
    <div
      className="kiosk-page"
      style={{
        '--kiosk-bg': uiBackgroundColor,
        '--kiosk-surface': uiSurfaceColor,
        '--kiosk-panel': uiSurfaceColor,
        '--kiosk-primary': uiPrimaryColor,
        '--kiosk-accent': uiAccentColor,
        '--kiosk-ink': uiTextColor,
        '--kiosk-muted': uiMutedColor,
        '--kiosk-category-size': `${Math.max(14, Number(uiCategoryTextSize) || 18)}px`,
        '--kiosk-product-name-size': `${Math.max(14, Number(uiProductNameTextSize) || 18)}px`,
        '--kiosk-heading-size': `${Math.max(18, Number(uiHeadingTextSize) || 28)}px`,
        '--kiosk-body-size': `${Math.max(14, Number(uiBodyTextSize) || 17)}px`,
      }}
    >
      {error && <div className="kiosk-alert">{error}</div>}
      {showConnectionBanner && (
        <div className={`kiosk-connection-banner is-${kioskConnectionState}`} role="status" aria-live="polite">
          <div className="kiosk-connection-banner-copy">
            <strong>{kioskConnectionState === 'offline' ? 'Hors ligne' : 'Mode degrade'}</strong>
            <span>{kioskConnectionMessage || DEFAULT_DEGRADED_MESSAGE}</span>
            {cachedCatalogTimestamp ? <small>Catalogue local charge depuis le {new Date(cachedCatalogTimestamp).toLocaleString('fr-FR')}</small> : null}
          </div>
          <button
            type="button"
            className="kiosk-cta secondary kiosk-connection-retry"
            onClick={() => setBootstrapReloadToken((current) => current + 1)}
            onPointerDown={handleTapFeedback}
          >
            Reessayer
          </button>
        </div>
      )}
      {terminalOrderId && !loading && !terminalPrintError && <TerminalPaymentDialog
        key={`${customerAccountId}:${terminalOrderId}`}
        orderId={terminalOrderId}
        fetchWithAuth={terminalFetch}
        customerMode
        retainPendingOnPaid
        readerId={readPendingTerminalPayment()?.readerId}
        onPaid={printTerminalOrder}
        onCancelled={cancelTerminalOrder}
        onClose={async () => {
          const pending = readPendingTerminalPayment();
          if (['ready', 'cancelled'].includes(pending?.state)) await cancelTerminalOrder(pending);
        }}
        title="Paiement par carte"
      />}
      {terminalOrderId && terminalPrintError && <div className="kiosk-order-feedback is-error" role="dialog" aria-label="Paiement confirmé">
        <div className="kiosk-order-feedback-card">
          <h2>Paiement confirmé</h2><p role="alert">{terminalPrintError}</p>
          <div className="kiosk-order-feedback-actions">
            <button type="button" className="kiosk-cta" disabled={terminalPrintingRef.current} onClick={async () => {
              const pending = readPendingTerminalPayment();
              if (pending?.context?.source === 'borne') await printTerminalOrder(pending, true);
            }}>Réimprimer après vérification</button>
            <button type="button" className="kiosk-cta secondary" onClick={async () => {
              const pending = readPendingTerminalPayment();
              if (pending?.state === 'paid') await finishTerminalOrder(pending);
            }}>Continuer sans réimprimer</button>
          </div>
        </div>
      </div>}
      {!terminalOrderId && checkoutState !== 'idle' && (
        <div className={`kiosk-order-feedback is-${checkoutState}`}>
          <div className="kiosk-order-feedback-card">
            {checkoutState === 'loading' && <div className="kiosk-spinner" />}
            {lastVisibleTicketNumber && (
              <div
                className={`kiosk-order-feedback-ticket ${checkoutState === 'success' ? 'is-success' : ''}`}
              >
                Commande n°{String(lastVisibleTicketNumber).padStart(4, '0')}
              </div>
            )}
            <h2
              style={
                checkoutState === 'success'
                  ? { fontSize: `${Math.max(24, Number(checkoutSuccessMessageSize) || 54)}px` }
                  : undefined
              }
            >
              {checkoutState === 'loading' && selectedPaymentMethod === 'cb' && cardPaymentStage === 'waiting_terminal' ? (
                <span className="kiosk-payment-terminal-instruction">
                  <span>Cliquez sur le bouton</span>
                  <span className="kiosk-payment-terminal-button-inline" aria-hidden="true">
                    <span className="kiosk-payment-terminal-button-ring" />
                  </span>
                  <span>du terminal de paiement</span>
                </span>
              ) : checkoutState === 'loading' && selectedPaymentMethod === 'cb' && cardPaymentStage === 'cancelling' ? (
                'Annulation du paiement'
              ) : checkoutState === 'loading' ? (
                'Validation de la commande'
              ) : checkoutState === 'success' ? (
                paymentDisplayMessage(checkoutMessage)
              ) : (
                selectedPaymentMethod === 'cb' ? 'Paiement impossible' : 'Impression impossible'
              )}
            </h2>
            {checkoutState === 'loading' && selectedPaymentMethod === 'cb' && cardPaymentStage === 'waiting_terminal' && (
              <div className="kiosk-payment-terminal-guide" aria-hidden="true">
                <div className="kiosk-payment-terminal-visual">
                  <div className="kiosk-payment-terminal-screen">CB</div>
                  <div className="kiosk-payment-terminal-keys">
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    <span />
                    <span className="is-green" />
                  </div>
                </div>
              </div>
            )}
            {checkoutState !== 'success' && <p>{paymentDisplayMessage(checkoutMessage)}</p>}
            {checkoutState === 'loading' && selectedPaymentMethod === 'cb' && cardPaymentStage === 'waiting_terminal' && (
              <div className="kiosk-order-feedback-actions kiosk-order-feedback-actions-single">
                <button
                  type="button"
                  className="kiosk-cta secondary"
                  onClick={cancelCardCheckout}
                  onPointerDown={handleTapFeedback}
                >
                  ANNULER
                </button>
              </div>
            )}
            {checkoutState === 'error' && (
              <div className="kiosk-order-feedback-actions">
                <button type="button" className="kiosk-cta" onClick={retryCheckout} onPointerDown={handleTapFeedback}>
                  Réessayer
                </button>
              <button
                type="button"
                className="kiosk-cta secondary"
                onClick={cancelCardCheckout}
                onPointerDown={handleTapFeedback}
              >
                  Annuler
                </button>
                <button
                  type="button"
                  className="kiosk-cta secondary"
                  onClick={
                    selectedPaymentMethod === 'cb'
                      ? pendingOrderRef.current?.paymentStatus === 'paid'
                        ? () => completeCheckoutSuccess('cb')
                        : cancelCardCheckout
                      : validateWithoutTicket
                  }
                  onPointerDown={handleTapFeedback}
                >
                  {selectedPaymentMethod === 'cb' && pendingOrderRef.current?.paymentStatus !== 'paid' ? 'Fermer' : 'Valider sans ticket'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="kiosk-loading">Chargement du menu en cours...</div>
      ) : (
        <div className="kiosk-shell">
        <main className="kiosk-main">
            <div className={`kiosk-category-bar ${showCart ? 'is-dimmed' : ''}`}>
              <div className="kiosk-category-primary">
                <div className="kiosk-section-head">
                  <button
                    type="button"
                    className="kiosk-account-button"
                    onClick={() => setShowCustomerAccount(true)}
                    onPointerDown={handleTapFeedback}
                  >
                    <strong>Mon compte</strong>
                    <small>
                      {currentCustomer
                        ? `${currentPoints} pt${currentPoints > 1 ? 's' : ''} · -${possibleCartDiscount} € possible`
                        : 'Non connecte'}
                    </small>
                  </button>
                </div>

                <div className="kiosk-tabs kiosk-tabs-large">
                  {sections.map((section) => (
                    <button
                      key={section.id}
                      type="button"
                      className={`kiosk-tab ${activeTab === section.id ? 'is-active' : ''} ${section.tabCoverImage ? 'has-cover' : ''}`}
                      style={section.tabCoverImage ? { '--tab-cover-image': `url("${section.tabCoverImage}")` } : undefined}
                      onClick={() => setActiveTab(section.id)}
                      onPointerDown={handleTapFeedback}
                    >
                      <span className="kiosk-tab-icon" aria-hidden="true">
                        {SECTION_EMOJIS[section.name] || '🍽️'}
                      </span>
                      <span>{section.name}</span>
                    </button>
                  ))}
                  <button
                    type="button"
                    className={`kiosk-tab ${activeTab === MENU_TAB_ID ? 'is-active' : ''}`}
                    onClick={() => setActiveTab(MENU_TAB_ID)}
                    onPointerDown={handleTapFeedback}
                  >
                    <span className="kiosk-tab-icon" aria-hidden="true">
                      {SECTION_EMOJIS.Menu}
                    </span>
                    <span>Menu</span>
                  </button>
                </div>
              </div>
            </div>
            {activeTab !== MENU_TAB_ID && activeTab !== DEALS_TAB_ID && (
              <div className="kiosk-subfilters-shell">
                <div className="kiosk-subfilters kiosk-subfilters-inline">
                  <button
                    type="button"
                    className={`kiosk-subfilter ${activeSubcategoryId === 'all' ? 'is-active' : ''}`}
                    onClick={() => setActiveSubcategoryId('all')}
                    onPointerDown={handleTapFeedback}
                  >
                    Tout
                  </button>
                  {sectionSubcategories.map((subcategory) => (
                    <button
                      key={subcategory.id}
                      type="button"
                      className={`kiosk-subfilter ${
                        String(activeSubcategoryId) === String(subcategory.id) ? 'is-active' : ''
                      } ${subcategory.buttonCoverImage ? 'has-cover' : ''}`}
                      style={
                        subcategory.buttonCoverImage
                          ? { '--subfilter-cover-image': `url("${subcategory.buttonCoverImage}")` }
                          : undefined
                      }
                      onClick={() => setActiveSubcategoryId(subcategory.id)}
                      onPointerDown={handleTapFeedback}
                    >
                      <span className="kiosk-subfilter-label">{subcategory.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <section className="kiosk-section">
              {currentCustomer && recentProducts.length > 0 && activeTab === DEALS_TAB_ID && (
                <section className="kiosk-recent-strip">
                  <div className="kiosk-recent-strip-head">
                    <span className="kiosk-eyebrow">Fidelite</span>
                    <h3>Vos derniers achats</h3>
                  </div>
                  <div className="kiosk-recent-products">
                    {recentProducts.map((product) => (
                      <article className="kiosk-recent-card" key={`recent-${product.id}`}>
                        <div className="kiosk-recent-card-copy">
                          <div className="kiosk-recent-card-header">
                            <span className="kiosk-recent-thumb">
                              <img
                                src={product.thumbnailImage || product.image || 'https://via.placeholder.com/96x96?text=Produit'}
                                alt={product.name}
                                className="kiosk-recent-thumb-image"
                                loading="lazy"
                                decoding="async"
                              />
                            </span>
                            <div className="kiosk-recent-card-title">
                              <strong>{product.name}</strong>
                              <span>{Number(product.price || 0).toFixed(2)} €</span>
                            </div>
                          </div>
                          <div className="kiosk-card-tags">
                            {getProductTags(product).map((tag) => (
                              <span key={`recent-${product.id}-${tag.id}`} className="kiosk-card-tag">
                                <span aria-hidden="true">{tag.emoji}</span>
                                <span>{tag.label}</span>
                              </span>
                            ))}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="kiosk-cta secondary"
                          onClick={() => openProductSupplementModal(products.find((catalogProduct) => catalogProduct.id === product.id) || product)}
                          onPointerDown={handleTapFeedback}
                        >
                          Reprendre
                        </button>
                      </article>
                    ))}
                  </div>
                </section>
              )}

              {activeTab === MENU_TAB_ID ? (
                <div className="kiosk-menu-grid">
                  {availableMenus.map((menu) => (
                    <article
                      className={`kiosk-menu-card ${menu.image ? 'has-image' : ''}`}
                      key={menu.id}
                      style={menu.thumbnailImage ? { '--menu-card-image': `url("${menu.thumbnailImage}")` } : undefined}
                    >
                      <div className="kiosk-menu-card-overlay" />
                      <div className="kiosk-menu-banner">Menu</div>
                      <h3>{menu.name}</h3>
                      {formatAvailabilityWindow(menu) ? <p>Disponible {formatAvailabilityWindow(menu)}</p> : null}
                      <p>{menu.description || 'Menu personnalisable à composer.'}</p>
                      <div className="kiosk-menu-footer">
                        <strong>{menu.price.toFixed(2)} €</strong>
                        <button
                          type="button"
                          className="kiosk-cta secondary"
                          onClick={() => openMenuCustomization(menu)}
                          onPointerDown={handleTapFeedback}
                        >
                          Personnaliser
                        </button>
                      </div>
                    </article>
                  ))}
                  {availableMenus.length === 0 && (
                    <div className="kiosk-empty-cart">Aucun menu disponible sur ce créneau.</div>
                  )}
                </div>
              ) : (
                <>
                  <div className="kiosk-grid">
                    {filteredProductRows.map((row) => {
                      if (row.type === 'banner') {
                        const bannerImage = row.subcategory?.bannerCoverImage || '';
                        return (
                          <article className={`kiosk-subcategory-banner ${bannerImage ? 'has-image' : ''}`} key={row.id}>
                            <div
                              className="kiosk-subcategory-banner-media"
                              style={bannerImage ? { '--subcategory-banner-image': `url("${bannerImage}")` } : undefined}
                              aria-hidden="true"
                            />
                            <div className="kiosk-subcategory-banner-copy">
                              <h3>{row.subcategory?.name || 'Sous-catégorie'}</h3>
                            </div>
                          </article>
                        );
                      }

                      const product = row.product;
                      return (
                        <article className="kiosk-card" key={row.id}>
                          <button
                            type="button"
                            className="kiosk-card-media kiosk-card-media-button"
                            onClick={() => openProductPreview(product)}
                            onPointerDown={handleTapFeedback}
                          >
                            <img
                              src={product.thumbnailImage || product.image || 'https://via.placeholder.com/640x420?text=Produit'}
                              alt={product.name}
                              className="kiosk-product-image"
                              loading="lazy"
                              decoding="async"
                            />
                            {product.isDealOfDay && (
                              <span className="kiosk-product-ribbon">DEAL DU JOUR</span>
                            )}
                          </button>
                          <div className="kiosk-card-body">
                            <div className="kiosk-card-topline">
                              <h3>{product.name}</h3>
                              <strong>{product.price.toFixed(2)} €</strong>
                            </div>
                            {formatAvailabilityWindow(product) ? (
                              <p className="kiosk-product-hours">Disponible {formatAvailabilityWindow(product)}</p>
                            ) : null}
                            <div className="kiosk-card-tags">
                              {getProductTags(product).map((tag) => (
                                <span key={`${product.id}-${tag.id}`} className="kiosk-card-tag">
                                  <span aria-hidden="true">{tag.emoji}</span>
                                  <span>{tag.label}</span>
                                </span>
                              ))}
                            </div>
                            <p>{product.description || 'Recette maison, préparée minute.'}</p>
                            <button
                              type="button"
                              className="kiosk-cta"
                              onClick={() => openProductSupplementModal(product)}
                              onPointerDown={handleTapFeedback}
                            >
                              Ajouter
                            </button>
                          </div>
                        </article>
                      );
                    })}
                    {filteredProducts.length === 0 && (
                      <div className="kiosk-empty-cart">Aucun produit disponible sur ce créneau.</div>
                    )}
                  </div>
                </>
              )}
            </section>
          </main>

          {canDisplayCartPanel && showCart && (
            <button
              type="button"
              className="kiosk-cart-backdrop"
              aria-label="Fermer le panier"
              onClick={() => setShowCart(false)}
            />
          )}

          <aside className={`kiosk-cart ${canDisplayCartPanel ? 'is-available' : ''} ${canDisplayCartPanel && showCart ? 'is-open' : 'is-hidden'}`}>
            <div className="kiosk-cart-head">
              <div>
                <span className="kiosk-eyebrow">Commande</span>
                <h2>Ton panier</h2>
              </div>
              <div className="kiosk-cart-head-actions">
                <button
                  type="button"
                  className="kiosk-cart-toggle kiosk-cart-abandon"
                  onClick={handleClearCartRequest}
                  onPointerDown={handleTapFeedback}
                >
                  Abandonner le panier
                </button>
                <button
                  type="button"
                  className="kiosk-cart-toggle"
                  onClick={() => setShowCart((prev) => !prev)}
                  onPointerDown={handleTapFeedback}
                >
                  {showCart ? 'Fermer' : 'Ouvrir'}
                </button>
              </div>
            </div>

            <div className="kiosk-cart-options-row">
              <button
                type="button"
                className="kiosk-cart-options-trigger"
                onClick={() => setShowOrderOptions(true)}
                onPointerDown={handleTapFeedback}
              >
                <span>Ajouter un commentaire</span>
                <small>{customerComment ? 'Commentaire enregistré' : 'Aucun commentaire'}</small>
              </button>

              <label className="kiosk-cart-cutlery-toggle" onPointerDown={handleTapFeedback}>
                <input
                  type="checkbox"
                  checked={includeCutlery}
                  onChange={(event) => setIncludeCutlery(event.target.checked)}
                />
                <span>Ajouter des couverts</span>
              </label>

              {currentCustomer && possibleCartDiscount > 0 && (
                <label className="kiosk-cart-cutlery-toggle kiosk-cart-loyalty-toggle" onPointerDown={handleTapFeedback}>
                  <input
                    type="checkbox"
                    checked={useLoyaltyDiscount}
                    onChange={(event) => setUseLoyaltyDiscount(event.target.checked)}
                  />
                  <span>Utiliser {appliedLoyaltyPoints || possibleCartDiscount * 30} pts (-{appliedLoyaltyDiscount || possibleCartDiscount} €)</span>
                </label>
              )}
            </div>

            {cart.length === 0 ? (
              <div className="kiosk-empty-cart">
                Ajoute un produit ou un menu pour commencer ta commande.
              </div>
            ) : (
              <div className="kiosk-cart-list">
                {cart.map((item, index) => {
                  const promoGiftDiscountForItem = Number(
                    promoGiftResolution.cartGiftDiscountByKey.get(item.cartKey) || 0
                  );
                  const lineTotal = Math.max(0, Number(item.totalPrice || 0) * Number(item.quantity || 0) - promoGiftDiscountForItem);
                  return (
                  <article
                    className={`kiosk-cart-item ${promoGiftDiscountForItem > 0 ? 'is-promo-offered' : ''}`}
                    key={`${item.id}-${item.isMenu}-${index}`}
                  >
                    <div className="kiosk-cart-item-row">
                      <div className="kiosk-cart-item-copy">
                        <div className="kiosk-cart-thumb">
                          <img
                            src={item.cartImage || item.thumbnailImage || item.image || 'https://via.placeholder.com/120x120?text=Plat'}
                            alt={item.name}
                            className="kiosk-cart-thumb-image"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <div>
                          <h3>{item.name}</h3>
                          {!item.isMenu && Array.isArray(item.selectedSupplements) && item.selectedSupplements.length > 0 && (
                            <div className="kiosk-cart-menu-items">
                              {item.selectedSupplements.map((supplement, supplementIndex) => (
                                <span key={`${item.cartKey}-supplement-${supplementIndex}`}>
                                  + {supplement.name} ({Number(supplement.price || 0).toFixed(2)} €)
                                </span>
                              ))}
                            </div>
                          )}
                          {item.isMenu && Array.isArray(item.customSelections) && item.customSelections.length > 0 && (
                            <div className="kiosk-cart-menu-items">
                              {item.customSelections.map((selection, selectionIndex) => (
                                <span key={`${item.id}-${selectionIndex}`}>
                                  {selection.sectionName}: {
                                    (Array.isArray(selection.products) && selection.products.length
                                      ? selection.products.map((product) => product?.name).filter(Boolean)
                                      : [selection.product?.name]).filter(Boolean).join(', ')
                                  }
                                </span>
                              ))}
                            </div>
                          )}
                          {promoGiftDiscountForItem > 0 && (
                            <div className="kiosk-cart-menu-items kiosk-cart-promo-note">
                              <span>🎁 1 article offert via le code promo (-{formatEuro(promoGiftDiscountForItem)})</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <strong>{formatEuro(lineTotal)}</strong>
                      <div className="kiosk-quantity">
                        <button type="button" onClick={() => requestQuantityChange(item, -1)} onPointerDown={handleTapFeedback}>
                          -
                        </button>
                        <span>{item.quantity}</span>
                        <button type="button" onClick={() => requestQuantityChange(item, 1)} onPointerDown={handleTapFeedback}>
                          +
                        </button>
                      </div>
                      <button
                        type="button"
                        className="kiosk-remove"
                        onClick={() => {
                          if (totalItemsInCart >= 4) {
                            askConfirmation({
                              title: 'Retirer cet article',
                              message: `${item.name} sera supprimé du panier.`,
                              confirmLabel: 'Oui, retirer',
                              onConfirm: () => removeFromCart(item.cartKey),
                            });
                            return;
                          }
                          removeFromCart(item.cartKey);
                        }}
                        onPointerDown={handleTapFeedback}
                      >
                        Retirer
                      </button>
                    </div>
                  </article>
                  );
                })}
                {promoGiftCartLine && (
                  <article className="kiosk-cart-item kiosk-cart-item-gift">
                    <div className="kiosk-cart-item-row">
                      <div className="kiosk-cart-item-copy">
                        <div className="kiosk-cart-thumb">
                          <img
                            src={promoGiftCartLine.cartImage || promoGiftCartLine.thumbnailImage || promoGiftCartLine.image || 'https://via.placeholder.com/120x120?text=Cadeau'}
                            alt={promoGiftCartLine.productName}
                            className="kiosk-cart-thumb-image"
                            loading="lazy"
                            decoding="async"
                          />
                        </div>
                        <div>
                          <h3>{promoGiftCartLine.name}</h3>
                          <div className="kiosk-cart-menu-items">
                            <span>Cadeau offert lié au code promo</span>
                          </div>
                        </div>
                      </div>
                      <strong>{formatEuro(0)}</strong>
                      <div className="kiosk-quantity">
                        <span>1</span>
                      </div>
                      <div className="kiosk-remove is-disabled" aria-hidden="true">
                        Offert
                      </div>
                    </div>
                  </article>
                )}
              </div>
            )}

            <button
              type="button"
              className="kiosk-pay-button"
              disabled={!cart.length || isCheckoutBlocked || checkoutState !== 'idle'}
              onClick={handleCheckout}
              onPointerDown={handleTapFeedback}
            >
              {isCheckoutBlocked ? 'Serveur indisponible' : 'Valider et payer'}
            </button>
          </aside>
        </div>
      )}

      {selectedMenu && (
        <div className="kiosk-modal-backdrop" onClick={closeMenuCustomization}>
          <div className="kiosk-modal" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-modal-head">
              <div>
                <span className="kiosk-eyebrow">Personnalisation</span>
                <h2>{selectedMenu.name}</h2>
              </div>
              <button type="button" className="kiosk-modal-close" onClick={closeMenuCustomization} onPointerDown={handleTapFeedback}>
                ×
              </button>
            </div>

            {visibleMenuSections.map((section) => (
              <section className="kiosk-modal-section" key={section.id}>
                <h3>{getSectionSelectionLabel(section.name, section.requiredQuantity)}</h3>
                <div className="kiosk-modal-grid kiosk-modal-grid-menu-products">
                  {section.products.map((product) => (
                    <button
                      key={product.id}
                      type="button"
                      className={`kiosk-choice-card ${
                        getMenuSectionSelections(menuSelections, section.id).some((selectedProduct) => selectedProduct.id === product.id)
                          ? 'is-selected'
                          : ''
                      }`}
                      onClick={() => handleMenuSelection(section, product)}
                      onPointerDown={handleTapFeedback}
                    >
                      <img
                        src={product.thumbnailImage || product.image || 'https://via.placeholder.com/320x220?text=Choix'}
                        alt={product.name}
                        className="kiosk-choice-image"
                        loading="lazy"
                        decoding="async"
                      />
                      <div className="kiosk-choice-copy">
                        <strong>{product.name}</strong>
                        <span>{product.description || 'Choix inclus dans la formule.'}</span>
                        <em>+{product.MenuProducts?.additionalPrice?.toFixed(2) || '0.00'} €</em>
                      </div>
                    </button>
                  ))}
                </div>
                {getMenuSectionSelections(menuSelections, section.id).length < section.requiredQuantity && (
                  <div className="kiosk-empty-cart kiosk-empty-modal-block">
                    {getMenuSelectionShortageMessage(section, getMenuSectionSelections(menuSelections, section.id).length)}
                  </div>
                )}
                {section.products.length === 0 && (
                  <div className="kiosk-empty-cart kiosk-empty-modal-block">
                    Aucun produit disponible dans cette section pour le moment.
                  </div>
                )}
              </section>
            ))}

            <div className="kiosk-modal-footer">
              <button type="button" className="kiosk-cta secondary" onClick={closeMenuCustomization} onPointerDown={handleTapFeedback}>
                Annuler
              </button>
              <button type="button" className="kiosk-cta" onClick={addCustomMenuToCart} onPointerDown={handleTapFeedback}>
                Ajouter ce menu
              </button>
            </div>
          </div>
        </div>
      )}

      {menuSelectionError && (
        <div className="kiosk-dialog-backdrop" onClick={() => setMenuSelectionError('')}>
          <div className="kiosk-dialog-card" onClick={(event) => event.stopPropagation()}>
            <h3>Choix incomplet</h3>
            <p>{menuSelectionError}</p>
            <button
              type="button"
              className="kiosk-cta"
              onClick={() => setMenuSelectionError('')}
              onPointerDown={handleTapFeedback}
            >
              Compris
            </button>
          </div>
        </div>
      )}

      {previewProduct && (
        <div className="kiosk-modal-backdrop" onClick={closeProductPreview}>
          <div className="kiosk-modal kiosk-modal-product-preview" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-modal-head">
              <div>
                <span className="kiosk-eyebrow">Produit</span>
                <h2>{previewProduct.name}</h2>
              </div>
              <button type="button" className="kiosk-modal-close" onClick={closeProductPreview} onPointerDown={handleTapFeedback}>
                ×
              </button>
            </div>

            <section className="kiosk-product-preview-hero">
              <div className="kiosk-product-preview-media">
                <img
                  src={previewProduct.detailImage || previewProduct.image || previewProduct.thumbnailImage || 'https://via.placeholder.com/960x640?text=Produit'}
                  alt={previewProduct.name}
                  className="kiosk-product-preview-image"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="kiosk-product-preview-copy">
                <strong>{Number(previewProduct.price || 0).toFixed(2)} €</strong>
                <p>{previewProduct.description || 'Recette maison, préparée minute.'}</p>
                <div className="kiosk-card-tags">
                  {getProductTags(previewProduct).map((tag) => (
                    <span key={`preview-${previewProduct.id}-${tag.id}`} className="kiosk-card-tag">
                      <span aria-hidden="true">{tag.emoji}</span>
                      <span>{tag.label}</span>
                    </span>
                  ))}
                </div>
                {Array.isArray(previewProduct.allergens) && previewProduct.allergens.length > 0 && (
                  <div className="kiosk-allergen-panel">
                    <strong>Allergènes</strong>
                    <p>{previewProduct.allergens.map((id) => PRODUCT_ALLERGEN_LABELS[id] || id).join(', ')}</p>
                  </div>
                )}
              </div>
            </section>

            {previewSupplementGroups.length > 0 && (
              <div className="kiosk-supplement-groups">
                {previewSupplementGroups.map((group) => (
                  <section className="kiosk-modal-section" key={`preview-${group.name}`}>
                    <h3>{group.name}</h3>
                    <div className="kiosk-supplement-list">
                      {group.items.map((supplement) => {
                        const checked = previewSupplementIds.includes(supplement.id);
                        return (
                          <label
                            key={`preview-supp-${supplement.id}`}
                            className={`kiosk-supplement-item ${checked ? 'is-selected' : ''}`}
                            onPointerDown={handleTapFeedback}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => togglePreviewSupplementSelection(supplement.id)}
                            />
                            <span className="kiosk-supplement-thumb">
                              <img
                                src={supplement.thumbnailImage || supplement.image || 'https://via.placeholder.com/96x96?text=+'}
                                alt={supplement.name}
                                className="kiosk-supplement-thumb-image"
                                loading="eager"
                                fetchPriority="high"
                                decoding="async"
                              />
                            </span>
                            <span className="kiosk-supplement-copy">
                              <strong>{supplement.name}</strong>
                              <em>{Number(supplement.price || 0).toFixed(2)} €</em>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </section>
                ))}
              </div>
            )}

            <div className="kiosk-modal-footer">
              <button type="button" className="kiosk-cta secondary" onClick={closeProductPreview} onPointerDown={handleTapFeedback}>
                Annuler
              </button>
              <button type="button" className="kiosk-cta" onClick={confirmPreviewProductAdd} onPointerDown={handleTapFeedback}>
                Ajouter {previewSelectedSupplements.length > 0 ? `(${previewTotalPrice.toFixed(2)} €)` : ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {supplementProduct && (
        <div className="kiosk-modal-backdrop" onClick={() => setSupplementProduct(null)}>
          <div className="kiosk-modal kiosk-modal-supplements" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-modal-head">
              <div>
                <span className="kiosk-eyebrow">Suppléments</span>
                <h2>Souhaitez-vous ajouter :</h2>
                <p className="kiosk-supplement-product-name">{supplementProduct.name}</p>
              </div>
              <button type="button" className="kiosk-modal-close" onClick={() => setSupplementProduct(null)} onPointerDown={handleTapFeedback}>
                ×
              </button>
            </div>

            <div className="kiosk-supplement-groups">
              {Object.values(
                (supplementProduct.Supplements || []).reduce((groups, supplement) => {
                  const key = supplement.SupplementCategory?.name || 'Suppléments';
                  if (!groups[key]) {
                    groups[key] = { name: key, items: [] };
                  }
                  groups[key].items.push(supplement);
                  return groups;
                }, {})
              ).map((group) => (
                <section className="kiosk-modal-section" key={group.name}>
                  <h3>{group.name}</h3>
                  <div className="kiosk-supplement-list">
                    {group.items.map((supplement) => {
                      const checked = selectedSupplementIds.includes(supplement.id);
                      return (
                        <label
                          key={supplement.id}
                          className={`kiosk-supplement-item ${checked ? 'is-selected' : ''}`}
                          onPointerDown={handleTapFeedback}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSupplementSelection(supplement.id)}
                          />
                          <span className="kiosk-supplement-thumb">
                            <img
                              src={supplement.thumbnailImage || supplement.image || 'https://via.placeholder.com/96x96?text=+'}
                              alt={supplement.name}
                              className="kiosk-supplement-thumb-image"
                              loading="eager"
                              fetchPriority="high"
                              decoding="async"
                            />
                          </span>
                          <span className="kiosk-supplement-copy">
                            <strong>{supplement.name}</strong>
                            <em>{Number(supplement.price || 0).toFixed(2)} €</em>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>

            <div className="kiosk-modal-footer">
              <button type="button" className="kiosk-cta secondary" onClick={() => setSupplementProduct(null)} onPointerDown={handleTapFeedback}>
                Annuler
              </button>
              <button type="button" className="kiosk-cta" onClick={confirmProductWithSupplements} onPointerDown={handleTapFeedback}>
                {selectedSupplementIds.length > 0 ? 'Valider' : 'Valider sans supplément'}
              </button>
            </div>
          </div>
        </div>
      )}

      {upsellProducts.length > 0 && (
        <div className="kiosk-modal-backdrop" onClick={() => setUpsellProducts([])}>
          <div className="kiosk-modal kiosk-modal-upsell" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-modal-head">
              <div>
                <span className="kiosk-eyebrow">Dernière envie</span>
                <h2>Souhaitez-vous ajouter :</h2>
              </div>
              <button type="button" className="kiosk-modal-close" onClick={() => setUpsellProducts([])} onPointerDown={handleTapFeedback}>
                ×
              </button>
            </div>

            <div className="kiosk-upsell-grid">
              {upsellProducts.map((product) => (
                <article className="kiosk-card kiosk-upsell-card" key={`upsell-${product.id}`}>
                  <div className="kiosk-card-media">
                    <img
                      src={product.thumbnailImage || product.image || 'https://via.placeholder.com/640x420?text=Produit'}
                      alt={product.name}
                      className="kiosk-product-image"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="kiosk-card-body">
                    <div className="kiosk-card-topline">
                      <h3>{product.name}</h3>
                      <strong>{Number(product.price).toFixed(2)} €</strong>
                    </div>
                    <p>{product.description || 'Suggestion rapide à ajouter à la commande.'}</p>
                    <div className="kiosk-card-tags">
                      {getProductTags(product).map((tag) => (
                        <span key={`upsell-${product.id}-${tag.id}`} className="kiosk-card-tag">
                          <span aria-hidden="true">{tag.emoji}</span>
                          <span>{tag.label}</span>
                        </span>
                      ))}
                    </div>
                    <button type="button" className="kiosk-cta" onClick={() => addToCart(product)} onPointerDown={handleTapFeedback}>
                      Ajouter
                    </button>
                  </div>
                </article>
              ))}
            </div>

            <div className="kiosk-modal-footer">
              <button type="button" className="kiosk-cta secondary" onClick={() => setUpsellProducts([])} onPointerDown={handleTapFeedback}>
                Retour
              </button>
              <button
                type="button"
                className="kiosk-cta"
                onClick={async () => {
                  if (isCheckoutBlocked) {
                    setPromoCodeError('Le serveur est indisponible. Reessaie quand la connexion est revenue.');
                    return;
                  }
                  setUpsellProducts([]);
                  try {
                    await continueCheckoutFlow();
                  } catch (error) {
                    console.error(error);
                    setCheckoutState('error');
                    setCheckoutMessage(error.message || 'Impossible de continuer la commande.');
                  }
                }}
                onPointerDown={handleTapFeedback}
                disabled={isCheckoutBlocked}
              >
                Valider et payer
              </button>
            </div>
          </div>
        </div>
      )}

      {showOrderOptions && (
        <div className="kiosk-modal-backdrop" onClick={() => setShowOrderOptions(false)}>
          <div className="kiosk-modal kiosk-modal-order-options" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-modal-head">
              <div>
                <span className="kiosk-eyebrow">Commande</span>
                <h2>Ajouter un commentaire</h2>
              </div>
              <button type="button" className="kiosk-modal-close" onClick={() => setShowOrderOptions(false)} onPointerDown={handleTapFeedback}>
                ×
              </button>
            </div>

            <div className="kiosk-order-options">
              <label className="kiosk-order-comment">
                <span>Commentaire pour la commande</span>
                <textarea
                  value={customerComment}
                  onChange={(event) => setCustomerComment(event.target.value)}
                  placeholder="Ex : sans oignons, bien chaud, sauce à part..."
                />
              </label>

              <label className="kiosk-order-cutlery" onPointerDown={handleTapFeedback}>
                <input
                  type="checkbox"
                  checked={includeCutlery}
                  onChange={(event) => setIncludeCutlery(event.target.checked)}
                />
                <span>Ajouter des couverts</span>
              </label>
            </div>

            <div className="kiosk-modal-footer">
              <button type="button" className="kiosk-cta secondary" onClick={() => setShowOrderOptions(false)} onPointerDown={handleTapFeedback}>
                Retour
              </button>
              <button type="button" className="kiosk-cta" onClick={() => setShowOrderOptions(false)} onPointerDown={handleTapFeedback}>
                Valider
              </button>
            </div>
          </div>
        </div>
      )}

      {showPaymentChoice && (
        <div className="kiosk-modal-backdrop" onClick={() => setShowPaymentChoice(false)}>
          <div className="kiosk-modal kiosk-modal-payment-choice" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-modal-head">
              <div>
                <span className="kiosk-eyebrow">Paiement</span>
                <h2>Choisir un mode de paiement</h2>
              </div>
              <button
                type="button"
                className="kiosk-modal-close"
                onClick={() => setShowPaymentChoice(false)}
                onPointerDown={handleTapFeedback}
              >
                ×
              </button>
            </div>

            <div className="kiosk-payment-choice-grid">
              {((terminalConfig?.enabled === true && (!terminalConfig?.deviceId || terminalConfig.configured)) || (terminalConfig?.enabled === false && !terminalConfig?.deviceId && !workstation.device && myposEnabled)) && (
                <button
                  type="button"
                  className="kiosk-choice-card kiosk-payment-choice-card"
                  disabled={isCheckoutBlocked || Number(totalPriceToPay || 0) <= 0}
                  onClick={async () => {
                    setShowPaymentChoice(false);
                    setSelectedPaymentMethod('cb');
                    try {
                      await finalizeCardCheckout();
                    } catch (err) {
                      console.error(err);
                      setCheckoutState('error');
                      setCheckoutMessage(err.message || 'Impossible de lancer le paiement CB.');
                    }
                  }}
                  onPointerDown={handleTapFeedback}
                >
                  <div className="kiosk-payment-choice-emoji" aria-hidden="true">💳</div>
                  <div className="kiosk-choice-copy kiosk-payment-choice-copy">
                    <strong>Paiement CB</strong>
                    <span>Envoi du montant sur le terminal de paiement.</span>
                  </div>
                </button>
              )}

              <button
                type="button"
                className="kiosk-choice-card kiosk-payment-choice-card"
                disabled={isCheckoutBlocked}
                onClick={async () => {
                  setShowPaymentChoice(false);
                  setSelectedPaymentMethod('espece');
                  try {
                    await finalizeCheckout({ autoMarkAsPaid: Number(totalPriceToPay || 0) <= 0 });
                  } catch (err) {
                    console.error(err);
                    setCheckoutState('error');
                    setCheckoutMessage(err.message || 'Impossible de valider la commande.');
                  }
                }}
                onPointerDown={handleTapFeedback}
              >
                <div className="kiosk-payment-choice-emoji" aria-hidden="true">💶</div>
                <div className="kiosk-choice-copy kiosk-payment-choice-copy">
                  <strong>Paiement espèces</strong>
                  <span>Validation immédiate de la commande.</span>
                </div>
              </button>
            </div>
            {terminalConfigError && <p role="alert">{terminalConfigError}</p>}
            {terminalConfig?.deviceId && (!terminalConfig.enabled || !terminalConfig.configured) ? <p role="alert">Le terminal de cette borne doit être configuré par l’équipe. Le paiement en espèces reste disponible au comptoir.</p> : null}

            <div className="kiosk-promo-zone">
              <label className="admin-field">
                <span>Code promo</span>
                <div className="kiosk-promo-row">
                  <input
                    type="text"
                    value={promoCodeInput}
                    inputMode="numeric"
                    readOnly
                    placeholder="Entrer le code"
                  />
                  <button
                    type="button"
                    className="kiosk-cta secondary"
                    onClick={applyPromoCode}
                    onPointerDown={handleTapFeedback}
                    disabled={promoCodeLoading || isCheckoutBlocked}
                  >
                    {promoCodeLoading ? '...' : 'Appliquer'}
                  </button>
                </div>
              </label>
              {isCheckoutBlocked && (
                <div className="kiosk-promo-offline-note">
                  Code promo et paiement temporairement indisponibles hors connexion.
                </div>
              )}
              <NumericKeypad
                value={promoCodeInput}
                maxLength={8}
                compact
                className="kiosk-promo-keypad"
                onChange={(nextCode) => {
                  setPromoCodeError('');
                  setPromoCodeInput(nextCode);
                }}
              />
              {promoCodeError && <div className="loyalty-entry-error">{promoCodeError}</div>}
              {appliedPromoCode && (
                <div className="admin-summary-card">
                  <strong>Code appliqué: {appliedPromoCode.code}</strong>
                  <p style={{ margin: '8px 0 0' }}>
                    {appliedPromoCode.rewardType === 'product'
                      ? `Cadeau: ${appliedPromoCode.product?.name || 'Produit offert'}`
                      : appliedPromoCode.rewardType === 'product_choice'
                        ? appliedPromoCode.selectedGiftProductId
                          ? `Cadeau choisi: ${
                            (appliedPromoCode.eligibleProducts || []).find(
                              (product) => String(product.id) === String(appliedPromoCode.selectedGiftProductId)
                            )?.name || 'Produit offert'
                          }`
                          : promoGiftChoiceRequired
                            ? 'Choix cadeau requis avant validation du panier.'
                            : `Cadeau: 1 produit offert parmi ${appliedPromoCode.eligibleProducts?.length || 0} article(s) éligible(s)`
                      : appliedPromoCode.rewardType === 'discount_percent'
                        ? `${Number(appliedPromoCode.rewardValue || 0)} % de remise`
                        : `${Number(appliedPromoCode.rewardValue || 0).toFixed(2)} € de remise`}
                  </p>
                </div>
              )}
            </div>

            <div className="kiosk-modal-footer">
              <button
                type="button"
                className="kiosk-cta secondary"
                onClick={() => setShowPaymentChoice(false)}
                onPointerDown={handleTapFeedback}
              >
                Retour
              </button>
            </div>
          </div>
        </div>
      )}

      {showPromoGiftChoiceModal && appliedPromoCode?.rewardType === 'product_choice' && (
        <div className="kiosk-modal-backdrop" onClick={cancelPromoGiftChoice}>
          <div className="kiosk-modal kiosk-modal-promo-choice" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-modal-head">
              <div>
                <span className="kiosk-eyebrow">Cadeau</span>
                <h2>Choisis ton produit offert</h2>
                <p>Sélectionne 1 cadeau dans cette liste pour continuer.</p>
              </div>
              <button
                type="button"
                className="kiosk-modal-close"
                onClick={cancelPromoGiftChoice}
                onPointerDown={handleTapFeedback}
              >
                ×
              </button>
            </div>

            <div className="kiosk-modal-grid kiosk-promo-choice-grid">
              {(appliedPromoCode.eligibleProducts || []).map((product) => (
                <button
                  key={`promo-choice-${product.id}`}
                  type="button"
                  className="kiosk-choice-card"
                  onClick={() => handleChoosePromoGift(product)}
                  onPointerDown={handleTapFeedback}
                >
                  <img
                    src={product.thumbnailImage || product.image || 'https://via.placeholder.com/320x220?text=Cadeau'}
                    alt={product.name}
                    className="kiosk-choice-image"
                    loading="lazy"
                    decoding="async"
                  />
                  <div className="kiosk-choice-copy">
                    <strong>{product.name}</strong>
                    <span>{product.description || 'Produit offert via ton bon cadeau.'}</span>
                  </div>
                </button>
              ))}
            </div>

            <div className="kiosk-modal-footer">
              <button
                type="button"
                className="kiosk-cta secondary"
                onClick={cancelPromoGiftChoice}
                onPointerDown={handleTapFeedback}
              >
                Annuler le code
              </button>
            </div>
          </div>
        </div>
      )}

      {showCustomerAccount && (
        <div className="kiosk-modal-backdrop" onClick={() => setShowCustomerAccount(false)}>
          <div className="kiosk-modal kiosk-modal-account" onClick={(event) => event.stopPropagation()}>
            <div className="kiosk-modal-head">
              <div>
                <span className="kiosk-eyebrow">Fidelisation</span>
                <h2>Mon compte</h2>
              </div>
              <button
                type="button"
                className="kiosk-modal-close"
                onClick={() => setShowCustomerAccount(false)}
                onPointerDown={handleTapFeedback}
              >
                ×
              </button>
            </div>

            {currentCustomer ? (
              <div className="kiosk-account-summary">
                <div className="kiosk-account-stat">
                  <span>Telephone</span>
                  <strong>{currentCustomer.phone}</strong>
                </div>
                <div className="kiosk-account-stat">
                  <span>Points disponibles</span>
                  <strong>{currentPoints}</strong>
                </div>
                <div className="kiosk-account-stat">
                  <span>Reduction possible</span>
                  <strong>{possibleDiscountEuros.toFixed(0)} €</strong>
                </div>
                <div className="kiosk-account-stat">
                  <span>Total depense</span>
                  <strong>{Number(currentCustomer.totalSpent || 0).toFixed(2)} €</strong>
                </div>
              </div>
            ) : (
              <div className="kiosk-empty-cart">
                Connecte-toi avec ton numero de telephone pour cumuler des points.
              </div>
            )}

            <div className="kiosk-modal-footer">
              {currentCustomer ? (
                <>
                  <button
                    type="button"
                    className="kiosk-cta secondary"
                    onClick={() => {
                      setCurrentCustomer(null);
                      customerRequestRef.current += 1;
                      clearCustomerSession();
                      setUseLoyaltyDiscount(false);
                      setAppliedPromoCode(null);
                      setShowCustomerAccount(false);
                    }}
                    onPointerDown={handleTapFeedback}
                  >
                    Se deconnecter
                  </button>
                  <button
                    type="button"
                    className="kiosk-cta"
                    onClick={() => {
                      setShowCustomerAccount(false);
                      navigate('/welcome');
                    }}
                    onPointerDown={handleTapFeedback}
                  >
                    Changer de compte
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    className="kiosk-cta secondary"
                    onClick={() => setShowCustomerAccount(false)}
                    onPointerDown={handleTapFeedback}
                  >
                    Fermer
                  </button>
                  <button
                    type="button"
                    className="kiosk-cta"
                    onClick={() => {
                      setShowCustomerAccount(false);
                      navigate('/welcome');
                    }}
                    onPointerDown={handleTapFeedback}
                  >
                    Se connecter
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {confirmDialog && (
        <div className="kiosk-dialog-backdrop" onClick={closeConfirmDialog}>
          <div className="kiosk-dialog-card" onClick={(event) => event.stopPropagation()}>
            <h3>{confirmDialog.title}</h3>
            <p>{confirmDialog.message}</p>
            <div className="kiosk-dialog-actions">
              <button type="button" className="kiosk-cta secondary" onClick={closeConfirmDialog} onPointerDown={handleTapFeedback}>
                Retour
              </button>
              <button
                type="button"
                className={`kiosk-cta ${confirmDialog.tone === 'danger' ? 'danger' : ''}`}
                onClick={async () => {
                  const onConfirm = confirmDialog.onConfirm;
                  closeConfirmDialog();
                  if (onConfirm) {
                    await onConfirm();
                  }
                }}
                onPointerDown={handleTapFeedback}
              >
                {confirmDialog.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {promoInfoDialog && (
        <div className="kiosk-dialog-backdrop" onClick={closePromoInfoDialog}>
          <div className="kiosk-dialog-card" onClick={(event) => event.stopPropagation()}>
            <h3>{promoInfoDialog.title}</h3>
            <p>{promoInfoDialog.message}</p>
            <button
              type="button"
              className="kiosk-cta"
              onClick={closePromoInfoDialog}
              onPointerDown={handleTapFeedback}
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {showInactivityPrompt && checkoutState === 'idle' && (
        <div className="kiosk-dialog-backdrop kiosk-dialog-backdrop-inactivity">
          <div className="kiosk-dialog-card kiosk-inactivity-dialog" onClick={(event) => event.stopPropagation()}>
            <h3>Vous etes toujours la ?</h3>
            <p>Retour a la veille dans {inactivityPromptCountdown} sec.</p>
            <button
              type="button"
              className="kiosk-cta"
              onClick={continueAfterInactivityPrompt}
              onPointerDown={handleTapFeedback}
            >
              Continuer
            </button>
          </div>
        </div>
      )}

      {cartFeedback && (
        <div key={cartFeedback.id} className="kiosk-cart-feedback" aria-live="polite">
          <span className="kiosk-cart-feedback-thumb">
            <img
              src={cartFeedback.image}
              alt=""
              className="kiosk-cart-feedback-image"
              loading="lazy"
              decoding="async"
            />
          </span>
          <span className="kiosk-cart-feedback-badge">+1</span>
          <span className="kiosk-cart-feedback-label">{cartFeedback.name}</span>
        </div>
      )}

      <div className="kiosk-admin-inline">
        <button
          type="button"
          className="kiosk-admin-shortcut"
          onClick={() => setShowAdminAccessModal(true)}
          onPointerDown={handleTapFeedback}
        >
          menu admin
        </button>
      </div>

      {showAdminAccessModal ? (
        <div className="kiosk-admin-modal-backdrop" role="dialog" aria-modal="true">
          <form className="kiosk-admin-modal" onSubmit={submitAdminAccess}>
            <button type="button" className="kiosk-modal-close kiosk-admin-modal-close" onClick={closeAdminAccessModal}>
              ×
            </button>
            <h2>Accès admin</h2>
            <p>Entre le mot de passe pour revenir au choix des interfaces.</p>
            <input
              type="password"
              value={adminAccessPassword}
              onChange={(event) => {
                setAdminAccessPassword(event.target.value);
                setAdminAccessError('');
              }}
              placeholder="Mot de passe"
              autoFocus
            />
            {adminAccessError ? <div className="kiosk-inline-error">{adminAccessError}</div> : null}
            <button type="submit" className="kiosk-cta">Valider</button>
          </form>
        </div>
      ) : null}

      {canDisplayFloatingCart && (
        <div className="kiosk-floating-actions">
          <button
            type="button"
            className="kiosk-floating-account"
            onClick={() => setShowCustomerAccount(true)}
            onPointerDown={handleTapFeedback}
          >
            <span className="kiosk-floating-account-logo" aria-hidden="true" />
            <span className="kiosk-floating-account-copy">
              <strong>Mon compte</strong>
              <small>{currentCustomer ? `${currentPoints} pts` : 'Connexion'}</small>
            </span>
          </button>
          <button
            type="button"
            className="kiosk-floating-cart"
            onClick={() => setShowCart((prev) => !prev)}
            onPointerDown={handleTapFeedback}
          >
            <span className="kiosk-floating-cart-copy">
              <strong>🛒 Panier {totalItemsInCart}</strong>
              <span>Prix total {totalPriceToPay} €</span>
            </span>
            <span className="kiosk-floating-cart-action">
              {showCart ? 'Fermer le panier' : 'Ouvrir le panier'}
            </span>
          </button>
        </div>
      )}
    </div>
  );
}

export default ClientView;
